import React from 'react';
import filterPropsEvents from './reactUtils/filterPropsEvents';

export enum IconFillStyles {
	original = "",
	none = "none",
	default = "default",
	dark = "dark",
	red = "red",
	yellow = "yellow",
	warningYellow = "warningYellow",
	green = "green",
	magenta = "magenta",
	white = "white",
	gray = "gray",
	offWhite = "offWhite",
	blue = "blue",
	steel900 = "steel900",
	gray400 = "gray400"
}

const fillColors = {
	[IconFillStyles.none]: "none",
	[IconFillStyles.default]: "#EBEBEB",
	[IconFillStyles.dark]: "#303030",
	[IconFillStyles.red]: "#EB5757",
	[IconFillStyles.yellow]: "#E2A42E",
	[IconFillStyles.warningYellow]: "#FBBC44",
	[IconFillStyles.green]: "#2CA15B",
	[IconFillStyles.magenta]: "#cf53ee",
	[IconFillStyles.white]: "#FFFFFF",
	[IconFillStyles.gray]: "#6C6C6C",
	[IconFillStyles.offWhite]: "#FFF6E4",
	[IconFillStyles.blue]: "#3D8DE9",
	[IconFillStyles.steel900]: "#ECEFF1",
	[IconFillStyles.gray400]: "#A6A6A6"
}

function fillColor(style?: IconFillStyles)
{
	return fillColors[style ?? IconFillStyles.default];
}

interface IBaseIconProps
{
	className?: string;
	scale?: number;
	content?: React.ReactNode;
	svg?: JSX.Element;
	onClick?: () => void;
	fillStyle?: IconFillStyles;
	tooltip?: string;
	transform?: string;
	rotate?: number;
	svgstyle?: React.CSSProperties;
}

function replaceInvalidProps(props: Record<string, any>)
{
	const replacedProps = { ...props };

	const replacementMap = {
		"stroke-width": "strokeWidth",
		"clip-rule": "clipRule",
		"fill-rule": "fillRule",
		"stroke-linecap": "strokeLinecap",
		"stroke-linejoin": "strokeLinejoin",
		"stop-color": "stopColor",
	};

	Object.keys(replacementMap).forEach(propName =>
	{
		if (replacedProps[propName])
		{
			replacedProps[replacementMap[propName]] = replacedProps[propName];
			delete replacedProps[propName];
		}
	});

	return replacedProps;
}

/**
 * a default wrapper component for presenting SVG icons for use in the UI
 * @param {{
 * className: String,
 * scale: Number,
 * content: *,
 * svg: *,
 * onClick: ()
 * }} props the icon expects and can use these special props:
 * - className: an additional class name to add to the wrapper div. *optional*
 * - scale: the svg image will be transformed and scaled by this number. *defaults to 1.* *optional*
 * - content: the actual svg image content. this will be injected into the <svg></svg> tag, so make sure to proveide only valid svg hierarchhy information *required*
 * - svg: the actual svg image. Does *NOT* get wrapped, and is rendered as-is
 * - onClick: a callback function that will be executed when the container div is clicked *optional*
 * @returns the SVG icon with extra functionality such as mouse interaction
 */
function BaseIcon(props: IBaseIconProps)
{
	// according to https://stackoverflow.com/a/61283065/618726
	const {
		// list here any prop to filter out
		fillStyle,
		svg,
		content,
		// anything else will remain inside 'filteredProps'
		...filteredProps } = { ...props };

	return (
		<div
			{...filteredProps}
			className={[
				"iconRenderContainer",
				(props.className ? props.className : ""),
				(props.onClick ? "clickable" : ""),
			].join(' ')}
			data-name={"icon-" + props["data-name"]}
			data-tooltip={props.tooltip ?? props["data-tooltip"]}
		>
			{
				props.svg ?
					<svg xmlns="http://www.w3.org/2000/svg"
						width={props.svg.props.width}
						height={props.svg.props.height}
						viewBox={props.svg.props.viewBox}
						style={{transform: props.transform ?? `${props.scale ? `scale(${props.scale ?? 1})` : ""} ${props.rotate ? `rotate(${props.rotate ?? 0}deg)` : ""}`}}
					>
						{
							// TODO: need to replace faulty properties in a recursive way, but not fill/stroke colors
							React.Children.map(props.svg.props.children, (child, idx) =>
							{
								const childElement = React.Children.only(child) as React.ReactElement<any>;
								const filteredChildProps = replaceInvalidProps({ ...child.props, key: idx });

								// replace colors
								if (props.fillStyle !== IconFillStyles.original && filteredChildProps.fill && filteredChildProps.fill !== IconFillStyles.none)
								{
									filteredChildProps.fill = fillColor(props.fillStyle);
								}

								if (props.fillStyle !== IconFillStyles.original && filteredChildProps.stroke)
								{
									filteredChildProps.stroke = fillColor(props.fillStyle);
								}

								return React.createElement(childElement.type, filteredChildProps);
							})
						}
					</svg>
					:
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="512"
						height="512"
						viewBox="0 0 512 512"
						transform={"scale(" + (props.scale ? props.scale : 1) + ")"}
						style={{ ...props.svgstyle, fill: "magenta" }}
					>
						{content}
					</svg>
			}
		</div>
	);
}

export function AudioFileIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.611 12.6136V5L18 9.61538L16.4438 12.1448L14.4164 10.6802V16.2C14.4164 18.4092 12.459 20 10.2082 20C7.95742 20 6 18.4092 6 16.2C6 13.9908 7.95742 12.4001 10.2082 12.4001C10.6951 12.4001 11.1683 12.4745 11.611 12.6136Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Chevron(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M19.293 7.29297L20.7072 8.70718L12.0001 17.4143L3.29297 8.70718L4.70718 7.29297L12.0001 14.5859L19.293 7.29297Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ChevronRight(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="9" height="14" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M5.58574 7.00008L0.292847 1.70718L1.70706 0.292969L8.41417 7.00008L1.70706 13.7072L0.292847 12.293L5.58574 7.00008Z" fill="#EBEBEB"/>
				</svg>

			}
		/>
	);
}

export function CollapseAll(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 20.9998V18.6665H16.3333L15.1667 20.9998H3.5Z" fill="#D9D9D9"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 13.9998V11.6665H21V13.9998H3.5Z" fill="#D9D9D9"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 6.99984V4.6665H21V6.99984H3.5Z" fill="#D9D9D9"/>
					<path d="M21 17.3335L25.0415 23.3335H16.9586L21 16.3335Z" fill="#D9D9D9"/>
				</svg>

			}
		/>
	);
}

export function ClearIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M9.52201 12L2 4.47799L4.47799 2L12 9.52201L19.522 2L22 4.47799L14.478 12L22 19.522L19.522 22L12 14.478L4.47799 22L2 19.522L9.52201 12Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function MaterialAssetIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 20.5C16.6944 20.5 20.5 16.6944 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5ZM12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="white" />
					<path d="M19 12.3809C19 16.0365 15.866 19 12 19C8.13401 19 5 16.0365 5 12.3809C5 8.72531 8.13401 13.484 12 13.484C15.866 13.484 19 8.72531 19 12.3809Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ImportProjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="31" height="39" viewBox="0 0 31 39" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M21.2076 0.707031H3.41667C1.52969 0.707031 0 2.23672 0 4.1237V34.8737C0 36.7607 1.52969 38.2904 3.41667 38.2904H27.3333C29.2203 38.2904 30.75 36.7607 30.75 34.8737V10.2494L21.2076 0.707031ZM3.41669 4.12305H17.0833V10.9577C17.0833 12.8447 18.613 14.3743 20.5 14.3743H27.3334V34.873H3.41669V4.12305ZM20.5 10.9577V4.83065L26.627 10.9577H20.5ZM17.0833 29.7493V21.9152L19.292 24.1239L21.708 21.708L15.375 15.375L9.04202 21.708L11.458 24.1239L13.6667 21.9152V29.7493H17.0833Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function UiTemplateIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M4 2C3.44772 2 3 2.44772 3 3V21C3 21.5523 3.44772 22 4 22H20C20.5523 22 21 21.5523 21 21V6.71429C21 6.44061 20.8878 6.17889 20.6897 5.99015L16.7897 2.27586C16.6037 2.09877 16.3568 2 16.1 2H4ZM6.85742 8.25014V6.25014H14.5717V8.25014H6.85742ZM6.85742 11.2499V13.2499H17.1431V11.2499H6.85742ZM6.85742 18.2501V16.2501H17.1431V18.2501H6.85742Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SyncUITemplateIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3 3C3 2.44772 3.44772 2 4 2H16.1C16.3568 2 16.6037 2.09877 16.7897 2.27586L20.6897 5.99015C20.8878 6.17889 21 6.44061 21 6.71429V13.2899C20.3663 13.1013 19.695 13 19 13C18.3571 13 17.7345 13.0867 17.1431 13.2489V11.2499H6.85742V13.2499H17.1398C15.4455 13.7157 14.0083 14.8025 13.0881 16.2501H6.85742V18.2501H12.2205C12.0766 18.8094 12 19.3958 12 20C12 20.695 12.1013 21.3663 12.2899 22H4C3.44772 22 3 21.5523 3 21V3ZM6.85742 6.25014V8.25014H14.5717V6.25014H6.85742Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M16.6772 18H18.2V18.8H15.4V16H16.2V17.3081C16.89 16.473 17.8783 16 19 16C21.2091 16 23 17.7909 23 20C23 22.2091 21.2091 24 19 24C16.7909 24 15 22.2091 15 20H15.8C15.8 21.7673 17.2327 23.2 19 23.2C20.7673 23.2 22.2 21.7673 22.2 20C22.2 18.2327 20.7673 16.8 19 16.8C18.0426 16.8 17.2214 17.2315 16.6772 18Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M15.15 15.75H16.45V16.6926C17.1427 16.0857 18.0259 15.75 19 15.75C21.3472 15.75 23.25 17.6528 23.25 20C23.25 22.3472 21.3472 24.25 19 24.25C16.6528 24.25 14.75 22.3472 14.75 20V19.75H16.05V20C16.05 21.6292 17.3708 22.95 19 22.95C20.6292 22.95 21.95 21.6292 21.95 20C21.95 18.3708 20.6292 17.05 19 17.05C18.3048 17.05 17.6919 17.2968 17.2198 17.75H18.45V19.05H15.15V15.75ZM15.65 16.25V18.55H17.95V18.25H16.1939L16.4732 17.8555C17.064 17.0212 17.9609 16.55 19 16.55C20.9054 16.55 22.45 18.0946 22.45 20C22.45 21.9054 20.9054 23.45 19 23.45C17.1787 23.45 15.687 22.0387 15.5589 20.25H15.2582C15.3868 22.2045 17.0129 23.75 19 23.75C21.0711 23.75 22.75 22.0711 22.75 20C22.75 17.9289 21.0711 16.25 19 16.25C17.9526 16.25 17.0351 16.6899 16.3927 17.4674L15.95 18.0032V16.25H15.65Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackStopIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="22" y="22" width="20" height="20" transform="rotate(-180 22 22)" fill="white" />
				</svg>
			}
		/>
	);
}

export function ResetIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M6.19306 7H10V9H3V2H5V5.27035C6.72511 3.18251 9.19577 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12H4C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C9.60638 4 7.55354 5.0787 6.19306 7Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function WarningTriangleIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" fill="none">
					<path strokeWidth={0.25} stroke="null" id="svg_1" fill="#EB5757" d="m19.2585,13.50583l-6.21125,-10.61813c-0.62355,-1.08103 -1.7858,-1.75199 -3.04273,-1.75347c-1.25707,-0.00146 -2.41972,0.66673 -3.05104,1.7535l-6.21359,10.62201c-0.64431,1.08333 -0.65888,2.44084 -0.03481,3.54804c0.62461,1.108 1.79412,1.79759 3.06623,1.80797l12.44673,0c1.28529,-0.0125 2.45261,-0.69997 3.07703,-1.80704c0.62376,-1.10574 0.60993,-2.46019 -0.03657,-3.55289zm-16.99121,0.90478l6.21819,-10.62981c0.3149,-0.54207 0.89245,-0.87399 1.51697,-0.87326c0.62445,0.00073 1.20179,0.33402 1.51149,0.87088l6.21615,10.62647c0.32584,0.55088 0.33289,1.22903 0.02048,1.78256c-0.31268,0.55447 -0.89733,0.8988 -1.54117,0.90505l-12.43086,-0.00013c-0.63006,-0.00505 -1.21588,-0.35045 -1.52867,-0.90545c-0.31245,-0.5542 -0.30516,-1.23382 0.01742,-1.77631zm7.73358,0.90851c0.48984,0 0.88693,-0.39686 0.88693,-0.88656c0,-0.48972 -0.39709,-0.88668 -0.88693,-0.88668c-0.48984,0 -0.88695,0.39696 -0.88695,0.88668c0,0.4897 0.3971,0.88656 0.88695,0.88656zm0.88957,-8.86645l-1.77389,0l0,6.20655l1.77389,0l0,-6.20655z" clipRule="evenodd" fillRule="evenodd"/>
				</svg>
			}
		/>
	);
}

export function InverseWarningTriangleIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" fill="none">

					<path id="svg_1" fill={"#F2BA4E"} d="m7.23621,4.7367c0.40003,-0.8 1.20003,-0.8 1.6,0l5.60001,9.6c0.4,0.6 0,1.4387 -0.8,1.4387l-11.19997,0c-0.80001,0 -1.20005,-0.8387 -0.8,-1.4387l5.59996,-9.6z" clipRule="evenodd" fillRule="evenodd"/>
					<g>
						<path id="svg_2" fill="#303030" d="m8.03623,14.1367c0.36819,0 0.66667,-0.2985 0.66667,-0.6667c0,-0.3682 -0.29848,-0.6666 -0.66667,-0.6666c-0.36819,0 -0.66667,0.2984 -0.66667,0.6666c0,0.3682 0.29848,0.6667 0.66667,0.6667z" clipRule="evenodd" fillRule="evenodd"/>
						<path id="svg_3" fill="#303030" d="m7.36956,7.47004l1.33334,0l0,4.66666l-1.33334,0l0,-4.66666z" clipRule="evenodd" fillRule="evenodd"/>
					</g>
				</svg>
			}
		/>
	);
}

export function CheckMarkIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="25" height="25" xmlns="http://www.w3.org/2000/svg" fill="none">
					<title>greencheckmark</title>
					<path stroke="#26BB66" id="svg_1" fill="#26BB66" d="m10.00711,15.72305l9.26649,-9.2665l1.41018,1.4102l-10.67667,10.67671l-5.69089,-5.69091l1.4102,-1.4102l4.2807,4.2807z" clipRule="evenodd" fillRule="evenodd"/>
				</svg>
			}
		/>
	);
}

export function CheckMarkIconThin(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M5.66252 8.33687L11.0834 2.91602L11.9083 3.74097L5.66252 9.98679L2.33337 6.65764L3.15833 5.83268L5.66252 8.33687Z" fill="black"/>
				</svg>
			}
		/>
	);
}

////////////////////////////////// Welcome Screen Icons ///////////////////////

export function RecentProjectsIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20.1818C16.5187 20.1818 20.1818 16.5187 20.1818 12C20.1818 7.48131 16.5187 3.81818 12 3.81818C7.48131 3.81818 3.81818 7.48131 3.81818 12C3.81818 16.5187 7.48131 20.1818 12 20.1818ZM16.5455 11H13.0001V6.54545H11.0001V13H16.5455V11Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function MyProjectsIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M23 20C23 18.6938 22.1652 17.5825 21 17.1707V6.82929C22.1652 6.41746 23 5.30622 23 4C23 2.34315 21.6569 1 20 1C18.6938 1 17.5825 1.83481 17.1707 3H6.82929C6.41746 1.83481 5.30622 1 4 1C2.34315 1 1 2.34315 1 4C1 5.30622 1.83481 6.41746 3 6.82929V17.1707C1.83481 17.5825 1 18.6938 1 20C1 21.6569 2.34315 23 4 23C5.30622 23 6.41746 22.1652 6.82929 21H17.1707C17.5825 22.1652 18.6938 23 20 23C21.6569 23 23 21.6569 23 20ZM19 6.82929V17.1707C18.1476 17.472 17.472 18.1476 17.1707 19H6.82929C6.52801 18.1476 5.85241 17.472 5 17.1707V6.82929C5.85241 6.52801 6.52801 5.85241 6.82929 5H17.1707C17.472 5.85241 18.1476 6.52801 19 6.82929ZM4 5C4.55228 5 5 4.55228 5 4C5 3.44772 4.55228 3 4 3C3.44772 3 3 3.44772 3 4C3 4.55228 3.44772 5 4 5ZM21 4C21 4.55228 20.5523 5 20 5C19.4477 5 19 4.55228 19 4C19 3.44772 19.4477 3 20 3C20.5523 3 21 3.44772 21 4ZM4 21C4.55228 21 5 20.5523 5 20C5 19.4477 4.55228 19 4 19C3.44772 19 3 19.4477 3 20C3 20.5523 3.44772 21 4 21ZM21 20C21 20.5523 20.5523 21 20 21C19.4477 21 19 20.5523 19 20C19 19.4477 19.4477 19 20 19C20.5523 19 21 19.4477 21 20Z" fill="white" />
					<path d="M17 16H7C7.13889 15 7 13 12 13C16.5833 13 16.8611 15 17 16Z" fill="white" />
					<circle cx="12" cy="10" r="2" fill="white" />
				</svg>
			}
		/>
	);
}

export function AllProjectsIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M23 20C23 18.6938 22.1652 17.5825 21 17.1707V6.82929C22.1652 6.41746 23 5.30622 23 4C23 2.34315 21.6569 1 20 1C18.6938 1 17.5825 1.83481 17.1707 3H6.82929C6.41746 1.83481 5.30622 1 4 1C2.34315 1 1 2.34315 1 4C1 5.30622 1.83481 6.41746 3 6.82929V17.1707C1.83481 17.5825 1 18.6938 1 20C1 21.6569 2.34315 23 4 23C5.30622 23 6.41746 22.1652 6.82929 21H17.1707C17.5825 22.1652 18.6938 23 20 23C21.6569 23 23 21.6569 23 20ZM19 6.82929V17.1707C18.1476 17.472 17.472 18.1476 17.1707 19H6.82929C6.52801 18.1476 5.85241 17.472 5 17.1707V6.82929C5.85241 6.52801 6.52801 5.85241 6.82929 5H17.1707C17.472 5.85241 18.1476 6.52801 19 6.82929ZM4 5C4.55228 5 5 4.55228 5 4C5 3.44772 4.55228 3 4 3C3.44772 3 3 3.44772 3 4C3 4.55228 3.44772 5 4 5ZM21 4C21 4.55228 20.5523 5 20 5C19.4477 5 19 4.55228 19 4C19 3.44772 19.4477 3 20 3C20.5523 3 21 3.44772 21 4ZM4 21C4.55228 21 5 20.5523 5 20C5 19.4477 4.55228 19 4 19C3.44772 19 3 19.4477 3 20C3 20.5523 3.44772 21 4 21ZM21 20C21 20.5523 20.5523 21 20 21C19.4477 21 19 20.5523 19 20C19 19.4477 19.4477 19 20 19C20.5523 19 21 19.4477 21 20Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SharedWithMeIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M2 20C2 17.3682 3.89347 15 6.5 15H13.5C16.1065 15 18 17.3682 18 20V22C18 22.5523 17.5523 23 17 23C16.4477 23 16 22.5523 16 22V20C16 18.2135 14.7595 17 13.5 17H6.5C5.24054 17 4 18.2135 4 20V22C4 22.5523 3.55228 23 3 23C2.44772 23 2 22.5523 2 22V20Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M9.5 6C8.11929 6 7 7.11929 7 8.5C7 9.88071 8.11929 11 9.5 11C10.8807 11 12 9.88071 12 8.5C12 7.11929 10.8807 6 9.5 6ZM5 8.5C5 6.01472 7.01472 4 9.5 4C11.9853 4 14 6.01472 14 8.5C14 10.9853 11.9853 13 9.5 13C7.01472 13 5 10.9853 5 8.5Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M18.0334 15.7446C18.1743 15.2106 18.7214 14.8919 19.2554 15.0329C21.4719 15.6178 22.9986 17.6511 23.0002 19.9547V21.9998C23.0002 22.552 22.5525 22.9998 22.0002 22.9998C21.448 22.9998 21.0002 22.552 21.0002 21.9998L21.0002 19.9562C21.0002 19.9561 21.0002 19.9563 21.0002 19.9562C20.9991 18.5335 20.0585 17.3133 18.7451 16.9667C18.2111 16.8257 17.8924 16.2786 18.0334 15.7446Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M15.0558 4.67129C15.2372 4.14965 15.8071 3.87385 16.3288 4.05528C18.0496 4.65379 19.0003 6.59669 19.0003 8.49978C19.0003 10.4029 18.0496 12.3458 16.3288 12.9443C15.8071 13.1257 15.2372 12.8499 15.0558 12.3283C14.8743 11.8066 15.1501 11.2367 15.6718 11.0553C16.3047 10.8352 17.0003 9.89709 17.0003 8.49978C17.0003 7.10248 16.3047 6.16441 15.6718 5.94429C15.1501 5.76286 14.8743 5.19292 15.0558 4.67129Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function LearnAndSupportIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M11.9671 12.8081L7 11V15.2459C8.06836 15.1692 9.40507 15.4425 11.8793 17C14.3599 15.4338 15.4414 15.2459 17 15.2459V11L11.9671 12.8081Z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
					<path d="M12.414 4.23053L21.0863 8.63186C21.844 9.0164 21.8072 10.1109 21.0254 10.4437L18.1111 11.6844L12.3144 13.8665C12.0859 13.9525 11.8338 13.952 11.6057 13.865L5.88889 11.6844L2.96694 10.4404C2.18643 10.1082 2.14813 9.01611 2.90344 8.62996L11.5062 4.23187C11.7912 4.08619 12.1286 4.0857 12.414 4.23053Z" fill="white" />
					<path d="M1.93059 17.4783L3.18808 15.422C3.58195 14.7779 4.52016 14.7859 4.90305 15.4366L6.11309 17.4928C6.50538 18.1595 6.02473 19 5.25124 19H2.78371C2.00264 19 1.52309 18.1446 1.93059 17.4783Z" fill="white" />
					<path d="M4 16V9" stroke="white" strokeLinejoin="round" />
				</svg>
			}
		/>
	);
}

export function DemoProjectsIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M13.3333 16.2626V18.0001C13.3333 19.841 11.8409 21.3334 9.99996 21.3334C8.15901 21.3334 6.66663 19.841 6.66663 18.0001V16.2626C6.66663 16.0999 6.60898 15.9223 6.47284 15.6886C6.38537 15.5384 6.27094 15.3734 6.0983 15.145L6.04023 15.0693C5.92496 14.919 5.67317 14.5909 5.58021 14.4653C4.63866 13.1936 4.16663 12.0583 4.16663 10.5001C4.16663 7.27842 6.7783 4.66675 9.99996 4.66675C13.2216 4.66675 15.8333 7.27842 15.8333 10.5001C15.8333 12.0924 15.3548 13.2708 14.4213 14.5401C14.3223 14.6746 14.0728 14.999 13.9249 15.1911L13.9248 15.1912L13.9246 15.1914C13.8669 15.2665 13.8247 15.3213 13.8132 15.3366C13.6246 15.5876 13.5074 15.7655 13.4312 15.92C13.3622 16.0598 13.3333 16.1678 13.3333 16.2626ZM11.703 15.8662C11.7455 15.6357 11.8246 15.4095 11.9365 15.1827C12.068 14.916 12.2353 14.662 12.4809 14.3352C12.4977 14.3129 12.5516 14.2428 12.6214 14.1521L12.6223 14.1509C12.7724 13.9558 12.9949 13.6664 13.0786 13.5526C13.8192 12.5457 14.1666 11.69 14.1666 10.5001C14.1666 8.19889 12.3011 6.33341 9.99996 6.33341C7.69877 6.33341 5.83329 8.19889 5.83329 10.5001C5.83329 11.6489 6.17218 12.4639 6.91971 13.4736C6.99995 13.582 7.22657 13.8774 7.34898 14.037L7.42786 14.14C7.63725 14.417 7.78538 14.6306 7.91302 14.8497C8.11181 15.191 8.24379 15.5237 8.30096 15.8686C8.82025 16.1803 9.38162 16.3334 9.99996 16.3334C10.6199 16.3334 11.1826 16.1795 11.703 15.8662ZM11.6666 18.0001V17.7188C11.1396 17.9058 10.5827 18.0001 9.99996 18.0001C9.41717 18.0001 8.86035 17.9058 8.33329 17.7188V18.0001C8.33329 18.9206 9.07948 19.6667 9.99996 19.6667C10.9204 19.6667 11.6666 18.9206 11.6666 18.0001Z" fill="white"/>
					<rect x="7" y="0.161499" width="1" height="2" transform="rotate(-9.29241 7 0.161499)" fill="white"/>
					<rect width="1" height="2" transform="matrix(-0.986877 -0.161473 -0.161473 0.986877 13.3098 0.161499)" fill="white"/>
					<rect x="0.98645" y="7.02576" width="1" height="2" transform="rotate(-65.2883 0.98645 7.02576)" fill="white"/>
					<rect width="1" height="2" transform="matrix(-0.418052 -0.908423 -0.908423 0.418052 19.3234 7.02576)" fill="white"/>
					<rect x="3" y="2.76404" width="1" height="2" transform="rotate(-49.8197 3 2.76404)" fill="white"/>
					<rect width="1" height="2" transform="matrix(-0.645195 -0.764018 -0.764018 0.645195 17.3098 2.76404)" fill="white"/>
				</svg>

			}
		/>
	);
}

export function WhatsNewIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M20 10H18V18C18 18.5523 18.4477 19 19 19C19.5523 19 20 18.5523 20 18V10ZM18 8H22V9V10V18C22 19.6569 20.6569 21 19 21C17.3431 21 16 19.6569 16 18V3H18V8Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M19 19V21H5C3.34315 21 2 19.6569 2 18V3H17V19H19ZM10 6V11H5V6H10ZM15 9V7H11V9H15ZM15 12V14H5V12H15ZM15 18V16H5V18H15Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Spinner(props: {
	size?: number,
	style?: React.CSSProperties,
	className?: string,
	hidden?: boolean,
	scale?: number,
	"data-name"?: string,
	[key: string]: any,
})
{
	const size = props.size ? props.size : 64;

	const style = {
		... props.style,
		width: size + "px",
		height: size + "px",
	};

	if (props.hidden)
	{
		style.display = "none";
	}

	return (
		<div className={[
			"spinnerContainer",
			(props.className ? props.className : ""),
		].join(' ')}
		data-name={props["data-name"] ?? "spinner"}
		style={style}
		{...filterPropsEvents(props)}
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 100 100"
				transform={"scale(" + (props.scale ? props.scale : 1) + ")"}
			>
				<circle
					cx="50"
					cy="50"
					r="45" />
			</svg>
		</div>
	);
}

export function TriangleIconRight(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M14 7L3.5 13.0622L3.5 0.937822L14 7Z" fill={fillColor(props.fillStyle)} />
				</svg>
			}
		/>
	);
}

export function VideoFileIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3 4C2.44772 4 2 4.44772 2 5V18C2 18.5523 2.44772 19 3 19H21C21.5523 19 22 18.5523 22 18V5C22 4.44772 21.5523 4 21 4H3ZM9.08374 15.9569L15.334 11.8L9.08374 7.64308L9.08374 15.9569Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackGotoEndIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12 12L2 2L2 22L12 12Z" fill="white" />
					<path d="M20 12L11 2L11 22L20 12Z" fill="white" />
					<rect width="2" height="20" transform="matrix(-1 0 0 1 22 2)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackGotoStartIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12 12L22 2L22 22L12 12Z" fill="white" />
					<path d="M4 12L13 2L13 22L4 12Z" fill="white" />
					<rect x="2" y="2" width="2" height="20" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackGotoNextBarIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M16 12L2 2L2 22L16 12Z" fill="white" />
					<rect width="2" height="20" transform="matrix(-1 0 0 1 22 2)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackGotoPreviousBarIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M8 12L22 2L22 22L8 12Z" fill="white" />
					<rect x="2" y="2" width="2" height="20" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackGotoNextFrameIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M22 12L8 2L8 22L22 12Z" fill="white" />
					<rect width="2" height="20" transform="matrix(-1 0 0 1 4 2)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackGotoPreviousFrameIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M2 12L16 2L16 22L2 12Z" fill="white" />
					<rect x="20" y="2" width="2" height="20" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackPauseIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="9" y="22" width="4" height="20" transform="rotate(-180 9 22)" fill="white" />
					<rect x="19" y="22" width="4" height="20" transform="rotate(-180 19 22)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlaybackPlayIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M22 12L5 2L5 22L22 12Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function RecordStartIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="12" cy="12" r="10" fill="white" />
				</svg>
			}
		/>
	);
}

export function PencilIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M16.4374 2C17.0921 2 17.7197 2.26142 18.1781 2.72342L21.279 5.82432C21.7407 6.286 22.0001 6.91217 22.0001 7.56508C22.0001 8.21799 21.7407 8.84416 21.279 9.30584L9.95751 20.6238C9.25902 21.4295 8.2689 21.9245 7.1346 22.0023H2V21.0023L2.00325 16.7873C2.08844 15.7328 2.57867 14.7523 3.3265 14.0934L14.6954 2.72463C15.1564 2.26083 15.7834 2 16.4374 2ZM7.06398 20.0048C7.59821 19.967 8.09549 19.7184 8.49479 19.2616L16.0567 11.6997L12.3023 7.94519L4.6961 15.5496C4.29095 15.9079 4.04031 16.4092 4 16.8678V20.0029L7.06398 20.0048ZM13.7167 6.53115L17.4709 10.2855L19.8648 7.89162C19.9514 7.80502 20.0001 7.68756 20.0001 7.56508C20.0001 7.4426 19.9514 7.32514 19.8648 7.23854L16.7611 4.13486C16.6755 4.04855 16.5589 4 16.4374 4C16.3158 4 16.1992 4.04855 16.1136 4.13486L13.7167 6.53115Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function UserIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3 19C3 16.2386 5.23858 14 8 14H16C18.7614 14 21 16.2386 21 19V21C21 21.5523 20.5523 22 20 22C19.4477 22 19 21.5523 19 21V19C19 17.3431 17.6569 16 16 16H8C6.34315 16 5 17.3431 5 19V21C5 21.5523 4.55228 22 4 22C3.44772 22 3 21.5523 3 21V19Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M12 4C10.3431 4 9 5.34315 9 7C9 8.65685 10.3431 10 12 10C13.6569 10 15 8.65685 15 7C15 5.34315 13.6569 4 12 4ZM7 7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7C17 9.76142 14.7614 12 12 12C9.23858 12 7 9.76142 7 7Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function UserAvatarDefaultIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23ZM19.3995 17.1246C20.4086 15.6703 21 13.9042 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 13.9042 3.59138 15.6703 4.6005 17.1246C5.72595 15.6381 8.3706 15 12 15C15.6294 15 18.274 15.6381 19.3995 17.1246ZM17.9647 18.7398C17.672 17.6874 15.5694 17 12 17C8.43062 17 6.328 17.6874 6.03532 18.7398C7.6233 20.1462 9.71194 21 12 21C14.2881 21 16.3767 20.1462 17.9647 18.7398ZM12 15C9.76086 15 8 13.4274 8 10C8 7.75576 9.5791 6 12 6C14.4142 6 16 7.92158 16 10.2C16 13.4796 14.2181 15 12 15ZM10 10C10 12.2693 10.8182 13 12 13C13.1777 13 14 12.2984 14 10.2C14 8.95042 13.2157 8 12 8C10.7337 8 10 8.81582 10 10Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function OrganizationIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M2 20C2 17.3682 3.89347 15 6.5 15H13.5C16.1065 15 18 17.3682 18 20V22C18 22.5523 17.5523 23 17 23C16.4477 23 16 22.5523 16 22V20C16 18.2135 14.7595 17 13.5 17H6.5C5.24054 17 4 18.2135 4 20V22C4 22.5523 3.55228 23 3 23C2.44772 23 2 22.5523 2 22V20Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M9.5 6C8.11929 6 7 7.11929 7 8.5C7 9.88071 8.11929 11 9.5 11C10.8807 11 12 9.88071 12 8.5C12 7.11929 10.8807 6 9.5 6ZM5 8.5C5 6.01472 7.01472 4 9.5 4C11.9853 4 14 6.01472 14 8.5C14 10.9853 11.9853 13 9.5 13C7.01472 13 5 10.9853 5 8.5Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M18.0334 15.7446C18.1743 15.2106 18.7214 14.8919 19.2554 15.0329C21.4719 15.6178 22.9986 17.6511 23.0002 19.9547V21.9998C23.0002 22.552 22.5525 22.9998 22.0002 22.9998C21.448 22.9998 21.0002 22.552 21.0002 21.9998L21.0002 19.9562C21.0002 19.9561 21.0002 19.9563 21.0002 19.9562C20.9991 18.5335 20.0585 17.3133 18.7451 16.9667C18.2111 16.8257 17.8924 16.2786 18.0334 15.7446Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M15.0558 4.67129C15.2372 4.14965 15.8071 3.87385 16.3288 4.05528C18.0496 4.65379 19.0003 6.59669 19.0003 8.49978C19.0003 10.4029 18.0496 12.3458 16.3288 12.9443C15.8071 13.1257 15.2372 12.8499 15.0558 12.3283C14.8743 11.8066 15.1501 11.2367 15.6718 11.0553C16.3047 10.8352 17.0003 9.89709 17.0003 8.49978C17.0003 7.10248 16.3047 6.16441 15.6718 5.94429C15.1501 5.76286 14.8743 5.19292 15.0558 4.67129Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function StarIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12 2L14.2451 8.90983H21.5106L15.6327 13.1803L17.8779 20.0902L12 15.8197L6.12215 20.0902L8.36729 13.1803L2.48944 8.90983H9.75486L12 2Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function FolderIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M2 8.5H21C21.5523 8.5 22 8.94772 22 9.5V19C22 19.5523 21.5523 20 21 20H3C2.44772 20 2 19.5523 2 19V8.5Z" fill="white" />
					<path d="M2 5C2 4.44772 2.44772 4 3 4H7.25L9 7H2V5Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ErrorIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.1346 3.49696C11.5203 2.83435 12.4775 2.83435 12.8632 3.49695L21.8606 18.955C22.2486 19.6217 21.7677 20.4581 20.9963 20.4581H3.0015C2.23014 20.4581 1.74921 19.6217 2.13724 18.955L11.1346 3.49696ZM12 18.5674C12.5523 18.5674 13 18.1197 13 17.5674C13 17.0151 12.5523 16.5674 12 16.5674C11.4477 16.5674 11 17.0151 11 17.5674C11 18.1197 11.4477 18.5674 12 18.5674ZM13 8H11V14.9434H13V8Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PendingIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12.9091 7.45455V3.86812C17 4.32034 20.1818 7.78859 20.1818 12C20.1818 16.5187 16.5187 20.1818 12 20.1818C7.48131 20.1818 3.81818 16.5187 3.81818 12C3.81818 9.79434 4.69378 7.7297 6.22545 6.20373L4.9422 4.91569C3.07149 6.77944 2 9.30598 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2H11.0909V7.45455H12.9091ZM11.9998 10.1821C11.8369 10.1821 11.6791 10.2035 11.5289 10.2437L8.09717 6.81201L6.81152 8.09766L10.2432 11.5293C10.203 11.6795 10.1816 11.8374 10.1816 12.0003C10.1816 13.0044 10.9956 13.8185 11.9998 13.8185C13.004 13.8185 13.818 13.0044 13.818 12.0003C13.818 10.9961 13.004 10.1821 11.9998 10.1821Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ModelFileIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M3 17.579V9L11 13.5V22.5L3 17.579Z" fill="#EBEBEB" />
					<path d="M21 17.5V9L13 13.5793V22.5L21 17.5Z" fill="#EBEBEB" />
					<path d="M21 6.73684L12.2652 2L3 6.73684L12 12L21 6.73684Z" fill="#EBEBEB" />
				</svg>
			}
		/>
	);
}

export function ImageFileIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3 4C2.44772 4 2 4.44772 2 5V19C2 19.5523 2.44772 20 3 20H21C21.5523 20 22 19.5523 22 19V5C22 4.44772 21.5523 4 21 4H3ZM8 12.5333L9.69763 15.2777L13.5 9.3335L19.1292 18.1335H7.87083L7.87094 18.1333H4.5359L8 12.5333ZM6 10C7.10457 10 8 9.10457 8 8C8 6.89543 7.10457 6 6 6C4.89543 6 4 6.89543 4 8C4 9.10457 4.89543 10 6 10Z" fill="#EBEBEB" />
				</svg>
			}
		/>
	);
}

export function HelpIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12ZM20.1818 12C20.1818 16.5187 16.5187 20.1818 12 20.1818C7.48131 20.1818 3.81818 16.5187 3.81818 12C3.81818 7.48131 7.48131 3.81818 12 3.81818C16.5187 3.81818 20.1818 7.48131 20.1818 12ZM12.0003 16.5439C12.5025 16.5439 12.9097 16.1369 12.9097 15.6348C12.9097 15.1327 12.5025 14.7257 12.0003 14.7257C11.4981 14.7257 11.0909 15.1327 11.0909 15.6348C11.0909 16.1369 11.4981 16.5439 12.0003 16.5439ZM11.0909 13.8182H12.9091C12.9091 13.0924 13.0231 12.9594 13.7702 12.5858C15.0686 11.9367 15.6364 11.2742 15.6364 9.72727C15.6364 7.74603 14.0767 6.54545 12 6.54545C9.99169 6.54545 8.36364 8.17351 8.36364 10.1818H10.1818C10.1818 9.17766 10.9958 8.36364 12 8.36364C13.1611 8.36364 13.8182 8.86946 13.8182 9.72727C13.8182 10.453 13.7042 10.5861 12.9571 10.9596C11.6587 11.6088 11.0909 12.2712 11.0909 13.8182Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function TrashIcon(props)
{
	props = {
		"data-name": "TrashIcon",
		...props,
	};

	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M9 1H15C16.1046 1 17 1.89543 17 3V4H20C21.1046 4 22 4.89543 22 6V8C22 9.10457 21.1046 10 20 10H19.9199L19 21C19 22.1046 18.1046 23 17 23H7C5.89543 23 5 22.1046 5.00345 21.083L4.07987 10H4C2.89543 10 2 9.10457 2 8V6C2 4.89543 2.89543 4 4 4H7V3C7 1.89543 7.89543 1 9 1ZM4 6H7H17H20V8H4V6ZM6.08649 10H17.9132L17.0035 20.917L17 21H7L6.08649 10ZM15 3V4H9V3H15Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SortDescendingIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M7 16.1775L6.99996 4.00005H4.99996L5 16.2097L3.30357 14.2769L2 15.6621L6.02144 20L10 15.6601L8.65069 14.2769L7 16.1775Z" fill="white" />
					<path d="M19.2393 8.85742V10H14.2002V8.85742H19.2393ZM19.166 3.70605L14.8496 10H13.8193V9.16016L18.1699 2.89062H19.166V3.70605ZM18.6631 2.89062V4.03809H13.8242V2.89062H18.6631Z" fill="white" />
					<path d="M16.666 14.1064L14.7324 20H13.1748L15.8164 12.8906H16.8076L16.666 14.1064ZM18.2725 20L16.334 14.1064L16.1777 12.8906H17.1787L19.835 20H18.2725ZM18.1846 17.3535V18.501H14.4297V17.3535H18.1846Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SortAscendingIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M7 7.8225L6.99996 19.9999H4.99996L5 7.79029L3.30357 9.72312L2 8.33792L6.02144 4L10 8.33987L8.65069 9.72311L7 7.8225Z" fill="white" />
					<path d="M16.666 4.10645L14.7324 10H13.1748L15.8164 2.89062H16.8076L16.666 4.10645ZM18.2725 10L16.334 4.10645L16.1777 2.89062H17.1787L19.835 10H18.2725ZM18.1846 7.35352V8.50098H14.4297V7.35352H18.1846Z" fill="white" />
					<path d="M19.2393 18.8574V20H14.2002V18.8574H19.2393ZM19.166 13.7061L14.8496 20H13.8193V19.1602L18.1699 12.8906H19.166V13.7061ZM18.6631 12.8906V14.0381H13.8242V12.8906H18.6631Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function DownloadIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 20H2V22H22V20Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M4 16H2V22H4V16Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M23 16H21V22H23V16Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M13 12.1775L13 5H11L11 12.2097L9.30357 10.2769L8 11.6621L12.0214 16L16 11.6601L14.6507 10.2769L13 12.1775Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function UploadIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 5H2V3H22V5Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M4 9H2V3H4V9Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M23 9H21V3H23V9Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M13 12.8225L13 20H11L11 12.7903L9.30357 14.7231L8 13.3379L12.0214 9L16 13.3399L14.6507 14.7231L13 12.8225Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SolidCirclePlayIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM9 6L17 12L9 18L9 6Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SolidCirclePauseIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM11 17H8L8 7H11L11 17ZM13 17H16L16 7H13L13 17Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SolidCircleCloseIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM17.7072 15.7611L15.7611 17.7072L12.0001 13.9462L8.23907 17.7072L6.29297 15.7611L10.054 12.0001L6.29297 8.23907L8.23907 6.29297L12.0001 10.054L15.7611 6.29297L17.7072 8.23907L13.9462 12.0001L17.7072 15.7611Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function WarningIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M17.9999 6.49999C19 4.5 21 4.5 21.9999 6.5L36 30.5C37 32 36 34.0968 34 34.0968H6.00002C3.99999 34.0968 2.99991 32 4.00002 30.5L17.9999 6.49999Z" fill="#EB5757"/>
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M20 30C20.9205 30 21.6667 29.2538 21.6667 28.3333C21.6667 27.4128 20.9205 26.6667 20 26.6667C19.0796 26.6667 18.3334 27.4128 18.3334 28.3333C18.3334 29.2538 19.0796 30 20 30Z" fill="white"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M18.3334 13.3333H21.6667V25H18.3334V13.3333Z" fill="white"/>
					</g>
				</svg>
			}
		/>
	);
}

export function TargetIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z" fill="white" />
					<rect x="11" y="2" width="2" height="6" fill="white" />
					<rect x="22" y="11" width="2" height="6" transform="rotate(90 22 11)" fill="white" />
					<rect x="11" y="16" width="2" height="6" fill="white" />
					<rect x="8" y="11" width="2" height="6" transform="rotate(90 8 11)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PingIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="12" cy="12" r="6" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ExitIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M4 4C4 2.89543 4.89543 2 6 2H17.4407C18.5452 2 19.4407 2.89543 19.4407 4V6C19.4407 6.55228 18.993 7 18.4407 7C17.8884 7 17.4407 6.55228 17.4407 6L17.4407 4L6 4V20H17.4407V18C17.4407 17.4477 17.8884 17 18.4407 17C18.993 17 19.4407 17.4477 19.4407 18V20C19.4407 21.1046 18.5452 22 17.4407 22H6C4.89543 22 4 21.1046 4 20V4Z" fill="#ECEFF1" />
					<path fillRule="evenodd" clipRule="evenodd" d="M13.798 9.86636L13.798 7L19.4744 12L13.798 17L13.798 14.1336L8.10156 14.1336L8.10156 9.86636L13.798 9.86636Z" fill="#ECEFF1" />
					<path fillRule="evenodd" clipRule="evenodd" d="M8.60156 13.6336L13.798 13.6336C14.0742 13.6336 14.298 13.8575 14.298 14.1336L14.298 15.8933L18.718 12L14.298 8.10672L14.298 9.86635C14.298 10.1425 14.0742 10.3664 13.798 10.3664L8.60156 10.3664L8.60156 13.6336ZM7.60156 14.1336L7.60156 9.86635C7.60156 9.73374 7.65424 9.60657 7.74801 9.5128C7.84178 9.41903 7.96895 9.36635 8.10156 9.36635L13.298 9.36635L13.298 6.99999C13.298 6.80351 13.4131 6.62523 13.5922 6.54434C13.7712 6.46344 13.9811 6.49492 14.1285 6.62479L19.8049 11.6248C19.9127 11.7197 19.9744 11.8564 19.9744 12C19.9744 12.1436 19.9127 12.2803 19.8049 12.3752L14.1285 17.3752C13.9811 17.5051 13.7712 17.5365 13.5922 17.4556C13.4131 17.3748 13.298 17.1965 13.298 17L13.298 14.6336L8.10156 14.6336C7.82542 14.6336 7.60156 14.4098 7.60156 14.1336Z" fill="#ECEFF1" />
				</svg>
			}
		/>
	);
}

export function DragHandleIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="8" cy="5" r="2" fill="white" />
					<circle cx="8" cy="12" r="2" fill="white" />
					<circle cx="8" cy="19" r="2" fill="white" />
					<circle cx="16" cy="5" r="2" fill="white" />
					<circle cx="16" cy="12" r="2" fill="white" />
					<circle cx="16" cy="19" r="2" fill="white" />
				</svg>
			}
		/>
	);
}

export function HomeIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M6.48902 10.3463L12 4.83533L17.511 10.3463H17.5092V19.529H6.4899V10.3463H6.48902ZM4.65335 12.182L3.2977 13.5376L2 12.2399L10.7025 3.53746C11.4191 2.82085 12.5809 2.82085 13.2975 3.53746L22 12.2399L20.7023 13.5376L19.3457 12.1811V19.529C19.3457 20.5433 18.5235 21.3656 17.5092 21.3656H6.4899C5.47561 21.3656 4.65335 20.5433 4.65335 19.529V12.182Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function GizmoMoveIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12.0002 0.585938L15.5644 4.15019L14.1502 5.5644L13.0002 4.41436V11.0002H19.5859L18.4359 9.85011L19.8501 8.4359L23.4144 12.0002L19.8501 15.5644L18.4359 14.1502L19.5859 13.0002H13.0002V19.5859L14.1502 18.4359L15.5644 19.8501L12.0002 23.4144L8.4359 19.8501L9.85011 18.4359L11.0002 19.5859V13.0002H4.41436L5.5644 14.1502L4.15019 15.5644L0.585938 12.0002L4.15019 8.4359L5.5644 9.85011L4.41436 11.0002H11.0002V4.41436L9.85011 5.5644L8.4359 4.15019L12.0002 0.585938Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Rotate3dIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M19.3184 2.86813L19.1704 5.05883C17.2816 3.13779 14.6743 2 11.8693 2C8.17168 2 4.81759 3.97711 3 7.11918L4.61156 8.05141C6.09967 5.4789 8.84309 3.86177 11.8693 3.86177C14.3025 3.86177 16.553 4.90733 18.1151 6.65442H15.5928V8.51618H20.8028L21.176 2.99363L19.3184 2.86813ZM3.97145 14.1012L11.8703 6.20242L19.7691 14.1012L11.8703 22L3.97145 14.1012ZM17.136 14.1011L11.8701 8.83524L6.60426 14.1011L11.8701 19.367L17.136 14.1011Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function GizmoScaleIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M9 15H4V20H9V15ZM2 13V22H11V13H2Z" fill="white" />
					<path d="M9 4H20V15H11V17H22V2H7V13H9V4Z" fill="white" />
					<path d="M18 9V6M18 6H15M18 6L12 12M12 12V9M12 12H15" stroke="white" />
				</svg>
			}
		/>
	);
}

export function GizmoNoneIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="white" />
					<rect x="17.6875" y="4.89941" width="2" height="18.0847" transform="rotate(45 17.6875 4.89941)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PerspectiveIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M4 8.72237V15.2776L20 17.6776V6.32237L4 8.72237ZM2 7V17L22 20V4L2 7Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function OrthographicIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M20 6H4V18H20V6ZM2 4V20H22V4H2Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PublishIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M16.5455 16.5911V18.4093H17.4545C19.9649 18.4093 22 16.3742 22 13.8639C22 11.5775 20.3119 9.68534 18.114 9.3659C17.3409 6.68715 14.8715 4.77295 12 4.77295C9.86676 4.77295 7.91729 5.83211 6.74536 7.54593C4.06094 7.89428 2 10.19 2 12.9548C2 15.9672 4.44208 18.4093 7.45455 18.4093V16.5911C5.44624 16.5911 3.81818 14.9631 3.81818 12.9548C3.81818 11.0006 5.36304 9.39763 7.30878 9.32126L7.80969 9.3016L8.06038 8.86748C8.86754 7.4697 10.3567 6.59113 12 6.59113C14.2283 6.59113 16.1185 8.20592 16.4827 10.3785L16.6125 11.1531L17.3977 11.1372C17.4261 11.1367 17.4261 11.1367 17.4545 11.1366C18.9608 11.1366 20.1818 12.3576 20.1818 13.8639C20.1818 15.3701 18.9608 16.5911 17.4545 16.5911H16.5455ZM12.9092 20.2274V14.2403L14.9936 16.3248L16.2793 15.0391L12.0001 10.7599L7.72088 15.0391L9.00653 16.3248L11.091 14.2403V20.2274H12.9092Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SpaceLocalIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M2.6998 21.2998L21.2998 21.2998L21.2998 2.69981L2.6998 2.6998L2.6998 21.2998ZM22.7998 22.7998L22.7998 1.19981L1.1998 1.1998L1.1998 22.7998L22.7998 22.7998Z" fill="white" />
					<path d="M11.9932 4.7998L14.3282 9.44967H9.65811L11.9932 4.7998Z" fill="white" />
					<path d="M4.24909 17.2365L7.34957 14.0421L8.48443 18.4057L4.24909 17.2365Z" fill="white" />
					<path d="M19.7487 17.2319L16.6678 14.0577L15.5402 18.3938L19.7487 17.2319Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11.421 13.8765V8.84082H12.721V13.8765L17.6322 16.045L17.107 17.2343L12.071 15.0106L7.03488 17.2343L6.50977 16.045L11.421 13.8765Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function SpaceWorldIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M22.023 18.5513L17.5378 13.9686L15.896 20.2287L22.023 18.5513Z" fill="white" />
					<path d="M1.94765 18.5513L6.43294 13.9686L8.07467 20.2287L1.94765 18.5513Z" fill="white" />
					<path d="M12.077 3L8.55124 8.88641L15.4763 8.8864L12.077 3Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11.0849 14.0674V8.71045H13.0849V14.0674L18.369 16.3812L17.5668 18.2133L12.0849 15.8129L6.603 18.2133L5.80078 16.3812L11.0849 14.0674Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlertIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.1346 3.49696C11.5203 2.83435 12.4775 2.83435 12.8632 3.49695L21.8606 18.955C22.2486 19.6217 21.7677 20.4581 20.9963 20.4581H3.0015C2.23014 20.4581 1.74921 19.6217 2.13724 18.955L11.1346 3.49696ZM12 18.5674C12.5523 18.5674 13 18.1197 13 17.5674C13 17.0151 12.5523 16.5674 12 16.5674C11.4477 16.5674 11 17.0151 11 17.5674C11 18.1197 11.4477 18.5674 12 18.5674ZM13 8H11V14.9434H13V8Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function GridIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<line x1="8" y1="2" x2="8" y2="22" stroke="white" strokeWidth="2" />
					<line x1="16" y1="2" x2="16" y2="22" stroke="white" strokeWidth="2" />
					<line x1="22" y1="8" x2="2" y2="8" stroke="white" strokeWidth="2" />
					<line x1="22" y1="16" x2="2" y2="16" stroke="white" strokeWidth="2" />
				</svg>
			}
		/>
	);
}

export function BillboardIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M7 22L7 2L9 2L9 22L7 22Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M15 22L15 2L17 2L17 22L15 22Z" fill="white" />
					<rect x="2" y="4" width="20" height="13" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlusIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="10" y="4" width="4" height="16" fill="white" />
					<rect x="20" y="10" width="4" height="16" transform="rotate(90 20 10)" fill="white" />
				</svg>
			}
		/>
	);
}

export function PlusIconThin(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M9 7H14V9H9V14H7V9H2V7H7V2H9V7Z" fill="#CDCDCD" />
				</svg>
			}
		/>
	);
}

export function MinusIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="20" y="10" width="4" height="16" transform="rotate(90 20 10)" fill="white" />
				</svg>
			}
		/>
	);
}

export function ShareIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M14 6C14 6.28742 14.0303 6.56777 14.0879 6.83801L9.01694 9.3735C8.28363 8.53189 7.20393 8 6 8C3.79086 8 2 9.79086 2 12C2 14.2091 3.79086 16 6 16C7.20393 16 8.28363 15.4681 9.01694 14.6265L14.0879 17.162C14.0303 17.4322 14 17.7126 14 18C14 20.2091 15.7909 22 18 22C20.2091 22 22 20.2091 22 18C22 15.7909 20.2091 14 18 14C16.7961 14 15.7164 14.5319 14.9831 15.3735L9.91208 12.838C9.96968 12.5678 10 12.2874 10 12C10 11.7126 9.96968 11.4322 9.91208 11.162L14.9831 8.6265C15.7164 9.46811 16.7961 10 18 10C20.2091 10 22 8.20914 22 6C22 3.79086 20.2091 2 18 2C15.7909 2 14 3.79086 14 6ZM8 12C8 13.1046 7.10457 14 6 14C4.89543 14 4 13.1046 4 12C4 10.8954 4.89543 10 6 10C7.10457 10 8 10.8954 8 12ZM18 8C19.1046 8 20 7.10457 20 6C20 4.89543 19.1046 4 18 4C16.8954 4 16 4.89543 16 6C16 7.10457 16.8954 8 18 8ZM20 18C20 19.1046 19.1046 20 18 20C16.8954 20 16 19.1046 16 18C16 16.8954 16.8954 16 18 16C19.1046 16 20 16.8954 20 18Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PositionLeftIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3.99997 2L2 2L2 21.9997L3.99997 21.9997L3.99997 2ZM21.9998 14.1428L21.9998 9.85712C21.9998 8.79431 21.1276 8 20.1174 8L7.88232 8C6.87218 8 6 8.79431 6 9.85712L6 14.1428C6 15.2056 6.87218 15.9999 7.88232 15.9999L20.1174 15.9999C21.1276 15.9999 21.9998 15.2056 21.9998 14.1428Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PositionCenterIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M13 8L20 8C21.0733 8 22 8.79432 22 9.85714L22 14.1429C22 15.2057 21.0733 16 20 16L13 16L13 22L11 22L11 16L4 16C2.9267 16 2 15.2057 2 14.1429L2 9.85714C2 8.79432 2.92671 8 4 8L11 8L11 2L13 2L13 8Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function PositionRightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M20 2L22 2L22 21.9997L20 21.9997L20 2ZM2.00025 14.1428L2.00025 9.85712C2.00025 8.79431 2.87243 8 3.88257 8L16.1177 8C17.1278 8 18 8.79431 18 9.85712L18 14.1428C18 15.2056 17.1278 15.9999 16.1177 15.9999L3.88257 15.9999C2.87243 15.9999 2.00025 15.2056 2.00025 14.1428Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignTextLeftIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M21.9736 7L21.9736 5L1.97363 5L1.97363 7L21.9736 7ZM15.9736 9.00021L15.9736 11.0002L1.97363 11.0002L1.97363 9.00021L15.9736 9.00021ZM21.9736 14.9999L21.9736 12.9999L1.97363 12.9999L1.97363 14.9999L21.9736 14.9999ZM15.9736 18.9999L15.9736 16.9999L1.97363 16.9999L1.97363 18.9999L15.9736 18.9999Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignTextCenterIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M21.9736 7L21.9736 5L1.97363 5L1.97363 7L21.9736 7ZM18.9736 9.00024L18.9736 11.0002L4.97363 11.0002L4.97363 9.00024L18.9736 9.00024ZM21.9736 14.9999L21.9736 12.9999L1.97363 12.9999L1.97363 14.9999L21.9736 14.9999ZM18.9736 19L18.9736 17L4.97363 17L4.97363 19L18.9736 19Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignTextRightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22.2119 6L22.2119 4L2.21191 4L2.21191 6L22.2119 6ZM22.2129 8L22.2129 10L8.21289 10L8.21289 8L22.2129 8ZM22.2119 13.9998L22.2119 11.9998L2.21191 11.9998L2.21191 13.9998L22.2119 13.9998ZM22.2129 18L22.2129 16L8.21289 16L8.21289 18L22.2129 18Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function EyeIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.9995 4.84912C14.4376 4.84912 16.7688 6.12789 18.9414 8.2289C19.6826 8.94568 20.3532 9.71091 20.9435 10.4765C21.2997 10.9384 21.5526 11.3018 21.691 11.5183L21.9991 11.9999L21.691 12.4815C21.5526 12.698 21.2997 13.0614 20.9435 13.5234C20.3532 14.2889 19.6826 15.0542 18.9414 15.7709C16.7688 17.872 14.4376 19.1507 11.9995 19.1507C9.56151 19.1507 7.23024 17.872 5.05763 15.7709C4.31644 15.0542 3.64588 14.2889 3.05559 13.5234C2.69939 13.0614 2.44649 12.698 2.30802 12.4815L2 11.9999L2.30802 11.5183C2.44649 11.3018 2.69939 10.9384 3.05559 10.4765C3.64588 9.71091 4.31644 8.94568 5.05763 8.2289C7.23024 6.12789 9.56151 4.84912 11.9995 4.84912ZM19.5279 11.5682C18.9874 10.8672 18.3729 10.166 17.6988 9.51407C15.8276 7.70449 13.8813 6.6369 11.9996 6.6369C10.118 6.6369 8.17172 7.70449 6.30049 9.51407C5.62634 10.166 5.01187 10.8672 4.47141 11.5682C4.35468 11.7195 4.24682 11.864 4.14821 12C4.24682 12.136 4.35468 12.2805 4.47141 12.4318C5.01187 13.1328 5.62634 13.834 6.30049 14.4859C8.17172 16.2955 10.118 17.3631 11.9996 17.3631C13.8813 17.3631 15.8276 16.2955 17.6988 14.4859C18.3729 13.834 18.9874 13.1328 19.5279 12.4318C19.6446 12.2805 19.7525 12.136 19.8511 12C19.7525 11.864 19.6446 11.7195 19.5279 11.5682ZM8.4248 12C8.4248 13.9746 10.0256 15.5754 12.0002 15.5754C13.9748 15.5754 15.5756 13.9746 15.5756 12C15.5756 10.0253 13.9748 8.42456 12.0002 8.42456C10.0256 8.42456 8.4248 10.0253 8.4248 12ZM13.7873 11.9999C13.7873 12.9872 12.9869 13.7876 11.9996 13.7876C11.0123 13.7876 10.2119 12.9872 10.2119 11.9999C10.2119 11.0125 11.0123 10.2122 11.9996 10.2122C12.9869 10.2122 13.7873 11.0125 13.7873 11.9999Z" fill="white" />
				</svg>
			}
		/>
	)
}

export function EyeIconOff(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3.69269 2.42969L2.42859 3.69378L6.06214 7.32732C5.72374 7.60778 5.38884 7.90897 5.05763 8.22926C4.31644 8.94604 3.64588 9.71127 3.05559 10.4768C2.69939 10.9388 2.44649 11.3022 2.30802 11.5187L2 12.0003L2.30802 12.4819C2.44649 12.6984 2.69939 13.0618 3.05559 13.5237C3.64588 14.2893 4.31644 15.0545 5.05763 15.7713C7.23024 17.8723 9.56151 19.1511 11.9995 19.1511C13.5377 19.1511 15.0334 18.6421 16.4735 17.7387L20.3056 21.5708L21.5697 20.3067L3.69269 2.42969ZM15.1677 16.4329L13.8157 15.0809C13.2836 15.3952 12.663 15.5755 12.0002 15.5755C10.0256 15.5755 8.4248 13.9748 8.4248 12.0001C8.4248 11.3374 8.60513 10.7167 8.9194 10.1846L7.33319 8.59838C6.98698 8.87759 6.64243 9.18357 6.30038 9.51435C5.62624 10.1663 5.01176 10.8675 4.4713 11.5684C4.35458 11.7198 4.24671 11.8643 4.1481 12.0003C4.24671 12.1363 4.35458 12.2807 4.4713 12.4321C5.01176 13.133 5.62624 13.8343 6.30038 14.4862C8.17161 16.2958 10.1179 17.3634 11.9995 17.3634C13.0436 17.3634 14.1076 17.0347 15.1677 16.4329ZM10.2728 11.538C10.2335 11.6854 10.2125 11.8403 10.2125 12.0001C10.2125 12.9874 11.0129 13.7878 12.0002 13.7878C12.16 13.7878 12.3149 13.7668 12.4623 13.7275L10.2728 11.538ZM19.2377 15.4781L17.9736 14.214C18.5404 13.641 19.0615 13.0364 19.5275 12.432C19.6442 12.2806 19.7521 12.1362 19.8507 12.0002C19.7521 11.8641 19.6442 11.7197 19.5275 11.5683C18.987 10.8674 18.3725 10.1662 17.6984 9.51424C15.8272 7.70466 13.8809 6.63707 11.9992 6.63707C11.5324 6.63707 11.0615 6.70279 10.5889 6.82926L9.1748 5.41521C10.0977 5.04725 11.0402 4.84937 11.9992 4.84937C14.4373 4.84937 16.7685 6.12813 18.9411 8.22915C19.6823 8.94593 20.3529 9.71116 20.9432 10.4767C21.2994 10.9387 21.5523 11.3021 21.6908 11.5186L21.9988 12.0002L21.6908 12.4818C21.5523 12.6983 21.2994 13.0617 20.9432 13.5236C20.4326 14.1857 19.8621 14.8476 19.2377 15.4781Z" fill="white" />
				</svg>
			}
		/>
	)
}

export function AnchorTopIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 5H2V3H22V5Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M13 10.8225L13 22H11L11 10.7903L9.30357 12.7231L8 11.3379L12.0214 7L16 11.3399L14.6507 12.7231L13 10.8225Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AnchorMiddleIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 13L2 13L2 11L22 11V13Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M12.4123 7.5391V2H11.5032V7.5391L8.64282 4.6787L8 5.32152L11.9578 9.2793L15.9156 5.32152L15.2727 4.6787L12.4123 7.5391Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M15.9156 5.32144L15.2728 4.67862L12.4124 7.53903V1.99992H11.5033V7.53903L8.6429 4.67862L8.00008 5.32144L11.9579 9.27922L15.9156 5.32144ZM11.0033 6.33192L8.6429 3.97151L7.29297 5.32144L11.9579 9.98633L16.6227 5.32144L15.2728 3.97151L12.9124 6.33192V1.49992H11.0033V6.33192Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M12.4123 16.7402V22.5H11.5032V16.7402L8.64282 19.6006L8 18.9578L11.9578 15L15.9156 18.9578L15.2727 19.6006L12.4123 16.7402Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M15.9156 18.9579L15.2728 19.6007L12.4124 16.7403V22.5001H11.5033V16.7403L8.6429 19.6007L8.00008 18.9579L11.9579 15.0001L15.9156 18.9579ZM11.0033 17.9474L8.6429 20.3078L7.29297 18.9579L11.9579 14.293L16.6227 18.9579L15.2728 20.3078L12.9124 17.9474V23.0001H11.0033V17.9474Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AnchorBottomIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 20H2V22H22V20Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M13 14.1775L13 3H11L11 14.2097L9.30357 12.2769L8 13.6621L12.0214 18L16 13.6601L14.6507 12.2769L13 14.1775Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Anchor9CornerIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M20 22L22 22L22 20L22 2L20 2L20 20L2 20L2 22L20 22Z" fill="#303030" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M4 2H2V4V22H4V4L22 4V2H4Z" fill="white" />
					<path d="M12 11.9999L6.28571 11.9999L6.28571 6.28564L12 6.28564L12 11.9999Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Anchor9SideIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M20 22L22 22L22 20L22 2L20 2L20 20L2 20L2 22L20 22Z" fill="#303030" />
						<path d="M2 2H4V22H2V2Z" fill="#303030" />
					</g>
					<path d="M14.7139 11.9999L8.99958 11.9999L8.99958 6.28564L14.7139 6.28564L14.7139 11.9999Z" fill="white" />
					<path d="M22 2L22 4L2 4L2 2L22 2Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Anchor9CenterIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M20 22L22 22L22 20L22 2L20 2L20 20L2 20L2 22L20 22Z" fill="#303030" />
						<path d="M2 2H4V22H2V2Z" fill="#303030" />
						<path d="M22 2L22 4L2 4L2 2L22 2Z" fill="#303030" />
					</g>
					<path d="M14.7139 14.7143L8.99958 14.7143L8.99958 9L14.7139 9L14.7139 14.7143Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ChainLinkClosedIcon(props)
{
	props = {
		"data-name": "ChainLinkClosedIcon",
		...props,
	};

	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M10 15V17H6C3.23858 17 1 14.7614 1 12C1 9.23858 3.23858 7 6 7H10V9H6C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15H10ZM14 9V7H18C20.7614 7 23 9.23858 23 12C23 14.7614 20.7614 17 18 17H14V15H18C19.6569 15 21 13.6569 21 12C21 10.3431 19.6569 9 18 9H14ZM17 11H7V13H17V11Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ChainLinkOpenedIcon(props)
{
	props = {
		"data-name": "ChainLinkOpenedIcon",
		...props,
	};

	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M10 17V15H6C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9H10V7H6C3.23858 7 1 9.23858 1 12C1 14.7614 3.23858 17 6 17H10ZM14 7V9H18C19.6569 9 21 10.3431 21 12C21 13.6569 19.6569 15 18 15H14V17H18C20.7614 17 23 14.7614 23 12C23 9.23858 20.7614 7 18 7H14Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function UndoIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M4 12.3658C4.49917 11.7119 5.15071 10.874 5.57082 10.4129C7.53403 8.25818 9.76148 7 12.5 7C18.8897 7 22 11.4122 22 17H20C20 12.3972 17.6052 9 12.5 9C10.4088 9 8.66775 9.98346 7.04921 11.7599C6.6264 12.224 5.82048 13.2824 5.26844 14H11V16H2V7H4V12.3658Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function RedoIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M20 12.3658C19.5008 11.7119 18.8493 10.874 18.4292 10.4129C16.466 8.25818 14.2385 7 11.5 7C5.11035 7 2 11.4122 2 17H4C4 12.3972 6.39481 9 11.5 9C13.5912 9 15.3322 9.98346 16.9508 11.7599C17.3736 12.224 18.1795 13.2824 18.7316 14H13V16H22V7H20V12.3658Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function RefreshIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M10 7H6.19306C7.55354 5.0787 9.60638 4 12 4C16.4183 4 20 7.58172 20 12H22C22 6.47715 17.5228 2 12 2C9.19577 2 6.72511 3.18251 5 5.27035V2H3V9H10V7ZM14 17H17.8069C16.4465 18.9213 14.3936 20 12 20C7.58172 20 4 16.4183 4 12H2C2 17.5228 6.47715 22 12 22C14.8042 22 17.2749 20.8175 19 18.7297V22H21V15H14V17Z" fill="white" />
				</svg>}
		/>
	);
}

export function ScopeARLogo(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="25" height="30" viewBox="0 0 25 30" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M1 8.25316V21.7468L12.6716 28M1 8.25316L12.6716 2L24 8.25316M1 8.25316L5.1194 10.5763M24 8.25316V21.7468L12.6716 28M24 8.25316L19.5373 10.8462M12.6716 28V23.0633M12.6716 14.8354V23.0633M12.6716 14.8354L5.1194 10.5763M12.6716 14.8354L19.5373 10.8462M12.6716 23.0633L19.5373 19.1139V14.8354M5.1194 10.5763V19.1139L8.55224 20.7595M19.5373 10.8462L12.6716 7.26582L9.58209 9.24051" stroke="#EEA904" strokeWidth="2" />
				</svg>
			}
		/>
	);
}

export function ToggleARIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M9 2V4H4V9.5H2V2H9Z" fill="white" />
					<path d="M2 15.5V22H9V20H4V15.5H2Z" fill="white" />
					<path d="M20 15.5V20H15V22H22V15.5H20Z" fill="white" />
					<path d="M22 9.5V2H15V4H20V9.5H22Z" fill="white" />
					<rect x="11" y="8" width="2" height="9" fill="white" />
					<rect x="16.5" y="11.5" width="2" height="9" transform="rotate(90 16.5 11.5)" fill="white" />
				</svg>
			}
		/>
	);
}

export function ToggleUIIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12.2012 7.04688H13.9102V13.6982C13.9102 14.4548 13.7461 15.0882 13.418 15.5986C13.0898 16.109 12.6432 16.4941 12.0781 16.7539C11.5176 17.0091 10.891 17.1367 10.1982 17.1367C9.48275 17.1367 8.84473 17.0091 8.28418 16.7539C7.72363 16.4941 7.28158 16.109 6.95801 15.5986C6.639 15.0882 6.47949 14.4548 6.47949 13.6982V7.04688H8.18848V13.6982C8.18848 14.1768 8.27051 14.571 8.43457 14.8809C8.59863 15.1862 8.83105 15.4118 9.13184 15.5576C9.43262 15.7035 9.78809 15.7764 10.1982 15.7764C10.6084 15.7764 10.9616 15.7035 11.2578 15.5576C11.5586 15.4118 11.791 15.1862 11.9551 14.8809C12.1191 14.571 12.2012 14.1768 12.2012 13.6982V7.04688Z" fill="#D9D9D9" />
					<path d="M17.2751 7.04688V17H15.5592V7.04688H17.2751Z" fill="#D9D9D9" />
				</svg>
			}
		/>
	);
}

export function ContentPlacementIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M4.19966 7.80039L5.61387 9.2146L2.82843 12L5.91385 15.0855L4.49963 16.4997L0 12L4.19966 7.80039Z" fill="white" />
					<path d="M8.09935 20.0994L11.9991 23.9991L16.1987 19.7995L14.7845 18.3852L11.9991 21.1707L9.51357 18.6852L8.09935 20.0994Z" fill="white" />
					<path d="M18.6842 9.51455L21.1697 12L18.3842 14.7855L19.7985 16.1997L23.9981 12L20.0984 8.10033L18.6842 9.51455Z" fill="white" />
					<path d="M16.4987 4.50061L11.9991 0.000976562L7.79938 4.20067L9.21359 5.61488L11.9991 2.8294L15.0845 5.91482L16.4987 4.50061Z" fill="white" />
					<rect x="12.1221" y="6" width="8" height="8" transform="rotate(45 12.1221 6)" fill="white" />
				</svg>
			}
		/>
	);
}

export function DeviceModeIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.4305 2.11696C11.711 1.96101 12.0522 1.96101 12.3328 2.11696L14.6541 3.40731C15.1023 3.65647 15.2637 4.2218 15.0145 4.67002C14.7654 5.11824 14.2001 5.27962 13.7518 5.03046L11.8816 3.99088L10.0114 5.03046C9.56321 5.27962 8.99788 5.11824 8.74872 4.67002C8.49957 4.2218 8.66095 3.65647 9.10917 3.40731L11.4305 2.11696ZM16.5218 5.25384C16.7823 4.81214 17.3516 4.66526 17.7933 4.92578L20.4665 6.50243C20.7496 6.6694 20.9233 6.97356 20.9233 7.30222V10.1887C20.9233 10.7015 20.5076 11.1172 19.9948 11.1172C19.4819 11.1172 19.0662 10.7015 19.0662 10.1887V8.92568L18.0978 9.49535C17.6558 9.75535 17.0867 9.60781 16.8267 9.1658C16.5667 8.72379 16.7142 8.15469 17.1562 7.89468L18.1652 7.30115L16.8499 6.52535C16.4082 6.26482 16.2613 5.69555 16.5218 5.25384ZM7.58795 5.31281C7.84847 5.75452 7.70159 6.32379 7.25988 6.58431L5.94455 7.36011L6.95356 7.95364C7.39557 8.21365 7.54311 8.78275 7.28311 9.22476C7.0231 9.66677 6.454 9.81432 6.01199 9.55431L5.04355 8.98464V10.2476C5.04355 10.7605 4.62783 11.1762 4.11501 11.1762C3.6022 11.1762 3.18648 10.7605 3.18648 10.2476V7.36118C3.18648 7.03253 3.36021 6.72836 3.6433 6.5614L6.31644 4.98475C6.75815 4.72422 7.32742 4.8711 7.58795 5.31281ZM8.88588 10.1676C9.14588 9.72556 9.71498 9.57801 10.157 9.83802L12.0075 10.9266L13.8581 9.83802C14.3001 9.57801 14.8692 9.72556 15.1292 10.1676C15.3892 10.6096 15.2417 11.1787 14.7997 11.4387L12.9361 12.5349V14.7894C12.9361 15.3023 12.5204 15.718 12.0075 15.718C11.4947 15.718 11.079 15.3023 11.079 14.7894V12.5349L9.21542 11.4387C8.77341 11.1787 8.62587 10.6096 8.88588 10.1676ZM3.97052 12.552C4.48334 12.552 4.89906 12.9677 4.89906 13.4805V15.7494L6.2407 16.5815C6.67649 16.8518 6.81065 17.4242 6.54035 17.86C6.27005 18.2958 5.69765 18.43 5.26186 18.1597L3.4811 17.0552C3.20808 16.8858 3.04199 16.5874 3.04199 16.2661V13.4805C3.04199 12.9677 3.45771 12.552 3.97052 12.552ZM20.029 12.6685C20.5418 12.6685 20.9576 13.0842 20.9576 13.597V16.3826C20.9576 16.7039 20.7915 17.0024 20.5185 17.1717L18.7377 18.2762C18.3019 18.5465 17.7295 18.4124 17.4592 17.9766C17.1889 17.5408 17.3231 16.9684 17.7589 16.6981L19.1005 15.8659V13.597C19.1005 13.0842 19.5162 12.6685 20.029 12.6685ZM12.0075 17.3573C12.5204 17.3573 12.9361 17.7731 12.9361 18.2859V19.493L13.8777 18.9695C14.326 18.7204 14.8913 18.8818 15.1404 19.33C15.3896 19.7782 15.2282 20.3435 14.78 20.5927L12.4587 21.883C12.1781 22.039 11.8369 22.039 11.5564 21.883L9.23508 20.5927C8.78686 20.3435 8.62548 19.7782 8.87463 19.33C9.12379 18.8818 9.68912 18.7204 10.1373 18.9695L11.079 19.493V18.2859C11.079 17.7731 11.4947 17.3573 12.0075 17.3573Z" fill="#ECEFF1" />
				</svg>
			}
		/>
	);
}

export function StandaloneModeIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.9051 2L20.7129 6.69126L12.0717 11.7655L3.57579 6.69156L11.9051 2ZM3 17.127V8.00975L11.4206 12.892V22.2366L3 17.127ZM21.0479 17.3186L21.0957 8.02158L12.6706 12.9998L12.6228 22.3928L21.0479 17.3186Z" fill="#EBEBEB" />
				</svg>
			}
		/>
	);
}

export function PivotAverageIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M21 13H13V21H21V13ZM11 11V23H23V11H11Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11 3H3V11H11V3ZM1 1V13H13V1H1Z" fill="white" />
					<rect x="7.31836" y="7.31836" width="10" height="10" fill="white" />
				</svg>
			}
		/>
	);
}

export function PivotLocalIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M21 13H13V21H21V13ZM11 11V23H23V11H11Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11 3H3V11H11V3ZM1 1V13H13V1H1Z" fill="white" />
					<rect x="4" y="4" width="6" height="6" fill="white" />
				</svg>
			}
		/>
	);
}

export function UINextIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M7 18.8438L8.4741 20.3207L17.125 11.6603L8.4741 3L7 4.47693L13.8956 11.6603L7 18.8438Z" fill="#ECEFF1" />
				</svg>
			}
		/>
	);
}

export function UIPreviousIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M17.125 18.8438L15.6509 20.3207L7 11.6603L15.6509 3L17.125 4.47693L10.2294 11.6603L17.125 18.8438Z" fill="#ECEFF1" />
				</svg>
			}
		/>
	);
}

export function AlignPivotXIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M1.94765 18.5513L6.43294 13.9686L8.07467 20.2287L1.94765 18.5513Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11.0849 14.0674V8.71045H13.0849V14.0674L18.369 16.3812L17.5668 18.2133L12.0849 15.8129L6.603 18.2133L5.80078 16.3812L11.0849 14.0674Z" fill="white" />
					<path d="M12.077 3L8.55124 8.88641L15.4763 8.8864L12.077 3Z" fill="white" />
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M17.5378 13.9685L22.023 18.5512L15.896 20.2286L16.5422 17.7645L12.085 15.8128L13.085 14.0673L17.0559 15.8061L17.5378 13.9685Z" fill="#EB5757" />
					</g>
				</svg>

			}
		/>
	);
}

export function AlignPivotYIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M1.94765 18.5513L6.43294 13.9686L8.07467 20.2287L1.94765 18.5513Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11.0849 14.0674V8.71045H13.0849V14.0674L18.369 16.3812L17.5668 18.2133L12.0849 15.8129L6.603 18.2133L5.80078 16.3812L11.0849 14.0674Z" fill="white" />
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M8.55078 8.8864L12.0765 3L15.4758 8.8864H13.0845V14.0674H11.0845V8.8864H8.55078Z" fill="#26BB66" />
					</g>
					<path d="M22.023 18.5513L17.5378 13.9686L15.896 20.2287L22.023 18.5513Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignPivotZIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12.077 3L8.55124 8.88641L15.4763 8.8864L12.077 3Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M11.0849 14.0674V8.71045H13.0849V14.0674L18.369 16.3812L17.5668 18.2133L12.0849 15.8129L6.603 18.2133L5.80078 16.3812L11.0849 14.0674Z" fill="white" />
					<path d="M22.023 18.5513L17.5378 13.9686L15.896 20.2287L22.023 18.5513Z" fill="white" />
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M6.43255 13.9685L1.94727 18.5512L8.07428 20.2286L7.44858 17.8427L12.0845 15.8128L11.0845 14.0673L6.93497 15.8843L6.43255 13.9685Z" fill="#26A8E1" />
					</g>
				</svg>
			}
		/>
	);
}

export function AlignBoundsLeftIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M5 7L12 3V11.5L5 15.5V7Z" fill="#EB5757" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>

			}
		/>
	);
}

export function AlignBoundsCenterXIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M8 9.5L16 5V14L8 18.5V9.5Z" fill="#EB5757" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsRightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M13 12L20 8V17L13 21V12Z" fill="#EB5757" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsTopIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M11.9033 2.7949L20.4033 7.59619L11.9033 12.3975L3.40332 7.59619L11.9033 2.7949Z" fill="#26BB66" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsCenterYIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M11.9033 6.7949L20.4033 11.5962L11.9033 16.3975L3.40332 11.5962L11.9033 6.7949Z" fill="#26BB66" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsBottomIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M11.9033 11.6987L20.4033 16.5L11.9033 21.3013L3.40332 16.5L11.9033 11.6987Z" fill="#26BB66" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsFrontIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M12 12L5 8V17L12 21V12Z" fill="#26A8E1" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsCenterZIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M16 9.42308L8 4.5V14.5769L16 19.5V9.42308Z" fill="#26A8E1" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AlignBoundsBackIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M19 8L12 4V12L19 16V8Z" fill="#26A8E1" />
					</g>
					<path fillRule="evenodd" clipRule="evenodd" d="M11.5097 2.12842C11.8142 1.95719 12.1858 1.95719 12.4903 2.12842L20.4903 6.62842C20.8051 6.80554 21 7.13873 21 7.5V16C21 16.3448 20.8224 16.6653 20.53 16.848L12.53 21.848C12.2057 22.0507 11.7943 22.0507 11.47 21.848L3.47 16.848C3.17762 16.6653 3 16.3448 3 16V7.5C3 7.13873 3.19486 6.80554 3.50974 6.62842L11.5097 2.12842ZM5 9.20985L11 12.5848V19.1958L5 15.4458V9.20985ZM13 19.1958L19 15.4458V9.20985L13 12.5848V19.1958ZM12 10.8527L17.9603 7.5L12 4.14735L6.03973 7.5L12 10.8527Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function CameraIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M14.9223 5.75049H3.84604C2.8265 5.75049 2 6.54993 2 7.5361V16.4641C2 17.4503 2.8265 18.2497 3.84604 18.2497H14.9223C15.9418 18.2497 16.7683 17.4503 16.7683 16.4641V14.9209L21.9995 17.3123V7.31289L16.7683 9.70431V7.5361C16.7683 6.54993 15.9418 5.75049 14.9223 5.75049Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function ModelObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M3 17.579V9L11 13.5V22.5L3 17.579Z" fill="#EBEBEB" />
					<path d="M21 17.5002V9.00024L13 13.5796V22.5002L21 17.5002Z" fill="#EBEBEB" />
					<path d="M21 6.73684L12.2652 2L3 6.73684L12 12L21 6.73684Z" fill="#EBEBEB" />
				</svg>
			}
		/>
	);
}

export function EmptyObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M0 10V6H2V10H0ZM0 2V0H2V2H0ZM10 0H6V2H10V0ZM24 2V0H22V2L24 2ZM14 0V2H18V0H14ZM24 6H22V10H24V6ZM24 14H22V18H24V14ZM24 22H22V24H24V22ZM18 24V22H14V24H18ZM10 24V22H6V24H10ZM2 24V22H0V24H2ZM0 18H2V14H0V18Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function Text3DObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M21.0936 5.32796H19.4936C19.4723 4.79463 19.3869 4.39997 19.2376 4.14396C18.9816 3.88796 18.3096 3.75996 17.2216 3.75996H13.1256V12.368C13.1256 14.8213 13.1256 16.528 13.1256 17.488C13.1256 18.448 13.1149 18.6506 13.0936 18.096C13.1149 19.0133 13.1469 19.8026 13.1896 20.464C13.2536 21.104 13.3389 21.616 13.4456 22H10.6616C10.8963 21.1893 11.0243 19.888 11.0456 18.096V12.368V3.72796H7.0136C5.94694 3.72796 5.2856 3.85596 5.0296 4.11197C4.88027 4.36796 4.79494 4.76263 4.7736 5.29597H3.1416C3.24827 3.95196 3.24827 2.87463 3.1416 2.06396H21.0936C21.0296 2.76796 21.0296 3.85596 21.0936 5.32796Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function AudioObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M21 0L8.78339 5.553H2V17.553H8.78339L21 23.106V0ZM10 15.9091L19 20V3.10601L10 7.19692V15.9091ZM4 7.553H8V15.553H4V7.553Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function CameraObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M16 6H2V18H16V6ZM0 4V20H18V4H0Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M18 10.386V13.614L22 15.114V8.886L18 10.386ZM16 9V15L24 18V6L16 9Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function LightObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M17.1429 16.698V19.2C17.1429 21.851 14.8403 24 12 24C9.15968 24 6.85714 21.851 6.85714 19.2V16.698C6.85714 16.4637 6.76821 16.208 6.55816 15.8714C6.42321 15.6552 6.24666 15.4176 5.98029 15.0887L5.89068 14.9796C5.71282 14.7633 5.32437 14.2907 5.18096 14.1099C3.72828 12.2787 3 10.6438 3 8.4C3 3.76081 7.02944 0 12 0C16.9706 0 21 3.76081 21 8.4C21 10.6929 20.2618 12.3898 18.8215 14.2176C18.6688 14.4113 18.2837 14.8784 18.0556 15.1551C17.9663 15.2634 17.9011 15.3425 17.8833 15.3646C17.5923 15.726 17.4115 15.9822 17.2939 16.2047C17.1875 16.406 17.1429 16.5616 17.1429 16.698ZM14.6278 16.1269C14.6933 15.795 14.8153 15.4694 14.9879 15.1428C15.1909 14.7588 15.4489 14.393 15.8279 13.9225C15.8538 13.8903 15.9373 13.7889 16.0452 13.658L16.046 13.6571L16.0462 13.6569C16.2777 13.3759 16.6209 12.9594 16.7501 12.7955C17.8926 11.3456 18.4287 10.1134 18.4287 8.3999C18.4287 5.08619 15.5505 2.3999 12.0001 2.3999C8.4497 2.3999 5.57153 5.08619 5.57153 8.3999C5.57153 10.0542 6.09438 11.2278 7.24772 12.6818C7.37152 12.8378 7.72115 13.2633 7.91003 13.4931L7.9101 13.4932L7.91103 13.4943C7.97469 13.5718 8.01989 13.6268 8.03172 13.6414C8.35477 14.0403 8.58332 14.3478 8.78026 14.6634C9.08701 15.1549 9.29066 15.6341 9.37884 16.1309C10.1799 16.5796 11.0459 16.7999 11.9997 16.7999C12.9564 16.7999 13.8247 16.5782 14.6278 16.1269ZM14.5715 19.1999V18.7947C13.7582 19.0642 12.899 19.1999 11.9997 19.1999C11.1007 19.1999 10.2417 19.0643 9.42868 18.795V19.1999C9.42868 20.5254 10.5799 21.5999 12.0001 21.5999C13.4203 21.5999 14.5715 20.5254 14.5715 19.1999Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function VideoObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M22 4H2L2 20H22V4ZM2 2C0.895431 2 0 2.89543 0 4V20C0 21.1046 0.89543 22 2 22H22C23.1046 22 24 21.1046 24 20V4C24 2.89543 23.1046 2 22 2H2Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M18 12L9 7L9 17L18 12ZM11 13.6667L11 10.3333L14 12L11 13.6667Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ImageObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M23.0004 2H3.00035V22H23.0004V2ZM1.00035 0V24H25.0004V0H1.00035Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M8.33373 9.25247L21.7075 22.6262L20.2933 24.0405L8.33373 12.0809L2.37417 18.0405L0.959961 16.6262L8.33373 9.25247Z" fill="white" />
					<path fillRule="evenodd" clipRule="evenodd" d="M18.3337 8.66675C19.4382 8.66675 20.3337 7.77132 20.3337 6.66675C20.3337 5.56218 19.4382 4.66675 18.3337 4.66675C17.2291 4.66675 16.3337 5.56218 16.3337 6.66675C16.3337 7.77132 17.2291 8.66675 18.3337 8.66675ZM18.3337 10.6667C20.5428 10.6667 22.3337 8.87589 22.3337 6.66675C22.3337 4.45761 20.5428 2.66675 18.3337 2.66675C16.1245 2.66675 14.3337 4.45761 14.3337 6.66675C14.3337 8.87589 16.1245 10.6667 18.3337 10.6667Z" fill="white" />
				</svg>
			}
		/>
	);
}

export function ModelRootObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M11.4305 2.11696C11.711 1.96101 12.0522 1.96101 12.3328 2.11696L14.6541 3.40731C15.1023 3.65647 15.2637 4.2218 15.0145 4.67002C14.7654 5.11824 14.2001 5.27962 13.7518 5.03046L11.8816 3.99088L10.0114 5.03046C9.56321 5.27962 8.99788 5.11824 8.74872 4.67002C8.49957 4.2218 8.66095 3.65647 9.10917 3.40731L11.4305 2.11696ZM16.5218 5.25384C16.7823 4.81214 17.3516 4.66526 17.7933 4.92578L20.4665 6.50243C20.7496 6.6694 20.9233 6.97356 20.9233 7.30222V10.1887C20.9233 10.7015 20.5076 11.1172 19.9948 11.1172C19.4819 11.1172 19.0662 10.7015 19.0662 10.1887V8.92568L18.0978 9.49535C17.6558 9.75535 17.0867 9.60781 16.8267 9.1658C16.5667 8.72379 16.7142 8.15469 17.1562 7.89468L18.1652 7.30115L16.8499 6.52535C16.4082 6.26482 16.2613 5.69555 16.5218 5.25384ZM7.58795 5.31281C7.84847 5.75452 7.70159 6.32379 7.25988 6.58431L5.94455 7.36011L6.95356 7.95364C7.39557 8.21365 7.54311 8.78275 7.28311 9.22476C7.0231 9.66677 6.454 9.81432 6.01199 9.55431L5.04355 8.98464V10.2476C5.04355 10.7605 4.62783 11.1762 4.11501 11.1762C3.6022 11.1762 3.18648 10.7605 3.18648 10.2476V7.36118C3.18648 7.03253 3.36021 6.72836 3.64329 6.5614L6.31644 4.98475C6.75815 4.72422 7.32742 4.8711 7.58795 5.31281ZM8.88588 10.1676C9.14588 9.72556 9.71498 9.57801 10.157 9.83802L12.0075 10.9266L13.8581 9.83802C14.3001 9.57801 14.8692 9.72556 15.1292 10.1676C15.3892 10.6096 15.2417 11.1787 14.7997 11.4387L12.9361 12.5349V14.7894C12.9361 15.3023 12.5204 15.718 12.0075 15.718C11.4947 15.718 11.079 15.3023 11.079 14.7894V12.5349L9.21543 11.4387C8.77341 11.1787 8.62587 10.6096 8.88588 10.1676ZM3.97052 12.552C4.48334 12.552 4.89906 12.9677 4.89906 13.4805V15.7494L6.2407 16.5815C6.67649 16.8518 6.81065 17.4242 6.54035 17.86C6.27005 18.2958 5.69765 18.43 5.26186 18.1597L3.4811 17.0552C3.20808 16.8858 3.04199 16.5874 3.04199 16.2661V13.4805C3.04199 12.9677 3.45771 12.552 3.97052 12.552ZM20.029 12.6685C20.5418 12.6685 20.9576 13.0842 20.9576 13.597V16.3826C20.9576 16.7039 20.7915 17.0024 20.5185 17.1717L18.7377 18.2762C18.3019 18.5465 17.7295 18.4124 17.4592 17.9766C17.1889 17.5408 17.3231 16.9684 17.7589 16.6981L19.1005 15.8659V13.597C19.1005 13.0842 19.5162 12.6685 20.029 12.6685ZM12.0075 17.3573C12.5204 17.3573 12.9361 17.7731 12.9361 18.2859V19.493L13.8777 18.9695C14.326 18.7204 14.8913 18.8818 15.1404 19.33C15.3896 19.7782 15.2282 20.3435 14.78 20.5927L12.4587 21.883C12.1781 22.039 11.8369 22.039 11.5564 21.883L9.23508 20.5927C8.78686 20.3435 8.62548 19.7782 8.87463 19.33C9.12379 18.8818 9.68912 18.7204 10.1373 18.9695L11.079 19.493V18.2859C11.079 17.7731 11.4947 17.3573 12.0075 17.3573Z" fill="#ECEFF1"/>
				</svg>
			}
		/>
	);
}

export function QuestionMarkSolidIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="7" cy="7" r="7" fill="#BE1F1F"/>
					<g>
						<path fillRule="evenodd" clipRule="evenodd" d="M6 8.42041V8.87762H8V8.42041C8 8.00979 8.07644 7.91076 8.6 7.55169C9.57644 6.88202 10 6.33332 10 5.21987C10 3.70479 8.65685 2.47656 7 2.47656C5.34315 2.47656 4 3.70479 4 5.21987H6C6 4.71484 6.44772 4.30544 7 4.30544C7.55228 4.30544 8 4.71484 8 5.21987C8 5.63049 7.92356 5.72952 7.4 6.08859C6.42356 6.75826 6 7.30696 6 8.42041ZM8.00045 10.705C8.00045 11.2101 7.55259 11.6195 7.00012 11.6195C6.44765 11.6195 5.99979 11.2101 5.99979 10.705C5.99979 10.2 6.44765 9.79061 7.00012 9.79061C7.55259 9.79061 8.00045 10.2 8.00045 10.705Z" fill="white"/>
					</g>
				</svg>
			}
		/>
	);
}

export function LightBulbIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M5.33366 6.79421V7.74984C5.33366 8.76236 4.51285 9.58317 3.50033 9.58317C2.4878 9.58317 1.66699 8.76236 1.66699 7.74984V6.79421C1.66699 6.70471 1.63529 6.60706 1.56041 6.47851C1.5123 6.39591 1.44937 6.30518 1.35441 6.17956L1.32248 6.13789C1.25908 6.05527 1.12059 5.87477 1.06946 5.80572C0.551611 5.10627 0.291992 4.48186 0.291992 3.62484C0.291992 1.85292 1.72841 0.416504 3.50033 0.416504C5.27224 0.416504 6.70866 1.85292 6.70866 3.62484C6.70866 4.5006 6.44549 5.14872 5.93205 5.84683C5.87765 5.92079 5.74047 6.09908 5.65914 6.20477L5.65901 6.20494C5.6272 6.24628 5.60396 6.27648 5.5976 6.28494C5.49386 6.42296 5.42941 6.52083 5.3875 6.6058C5.34957 6.68269 5.33366 6.74211 5.33366 6.79421ZM4.43702 6.57619C4.4604 6.44941 4.50387 6.32503 4.56542 6.20026C4.63777 6.05359 4.72977 5.91387 4.86486 5.73416C4.8741 5.72187 4.90378 5.68327 4.94217 5.63336L4.94259 5.63281L4.9427 5.63267C5.02524 5.52536 5.14757 5.3663 5.1936 5.30372C5.60088 4.74995 5.79199 4.27929 5.79199 3.62484C5.79199 2.35918 4.76598 1.33317 3.50033 1.33317C2.23467 1.33317 1.20866 2.35918 1.20866 3.62484C1.20866 4.25667 1.39504 4.70495 1.80619 5.26027C1.85033 5.31989 1.97499 5.48242 2.04231 5.5702L2.08567 5.62681C2.20083 5.77916 2.28231 5.89661 2.35251 6.01714C2.46184 6.20485 2.53443 6.38781 2.56587 6.57754C2.85149 6.74897 3.16024 6.83317 3.50033 6.83317C3.8413 6.83317 4.15078 6.74853 4.43702 6.57619ZM4.41699 7.74984V7.59512C4.12711 7.69801 3.82086 7.74984 3.50033 7.74984C3.17979 7.74984 2.87354 7.69801 2.58366 7.59512V7.74984C2.58366 8.2561 2.99406 8.6665 3.50033 8.6665C4.00659 8.6665 4.41699 8.2561 4.41699 7.74984Z" fill="#C4C4C4"/>
				</svg>
			}
		/>
	);
}

export function DocumentIconPDF(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="50" height="64" viewBox="0 0 50 64" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M49 4C49 2.34315 47.6569 1 46 1H15.5357C14.7767 1 14.0459 1.28769 13.4906 1.80509L1.95492 12.5533C1.34588 13.1208 1 13.9158 1 14.7482V60C1 61.6568 2.34315 63 4 63H46C47.6569 63 49 61.6569 49 60V4Z" fill="white" stroke="#FA0F00" strokeWidth="2"/>
						<path d="M14.5 13.5V1.15061L1.26851 13.5H14.5Z" fill="#FA0F00" stroke="#FA0F00"/>
						<path d="M17.4805 35.1055H15.3594V33.5176H17.4805C17.7695 33.5176 18.0039 33.4688 18.1836 33.3711C18.3633 33.2734 18.4941 33.1387 18.5762 32.9668C18.6621 32.791 18.7051 32.5938 18.7051 32.375C18.7051 32.1406 18.6621 31.9238 18.5762 31.7246C18.4941 31.5254 18.3633 31.3652 18.1836 31.2441C18.0039 31.1191 17.7695 31.0566 17.4805 31.0566H16.1328V38H14.0762V29.4688H17.4805C18.1641 29.4688 18.752 29.5938 19.2441 29.8438C19.7402 30.0938 20.1211 30.4375 20.3867 30.875C20.6523 31.3086 20.7852 31.8047 20.7852 32.3633C20.7852 32.9141 20.6523 33.3945 20.3867 33.8047C20.1211 34.2148 19.7402 34.5352 19.2441 34.7656C18.752 34.9922 18.1641 35.1055 17.4805 35.1055ZM24.6641 38H22.7715L22.7832 36.418H24.6641C25.0547 36.418 25.3848 36.3242 25.6543 36.1367C25.9277 35.9492 26.1348 35.6699 26.2754 35.2988C26.4199 34.9238 26.4922 34.4629 26.4922 33.916V33.5469C26.4922 33.1367 26.4512 32.7773 26.3691 32.4688C26.291 32.1562 26.1738 31.8965 26.0176 31.6895C25.8613 31.4785 25.666 31.3203 25.4316 31.2148C25.2012 31.1094 24.9355 31.0566 24.6348 31.0566H22.7363V29.4688H24.6348C25.2129 29.4688 25.7422 29.5684 26.2227 29.7676C26.707 29.9629 27.125 30.2441 27.4766 30.6113C27.832 30.9746 28.1055 31.4062 28.2969 31.9062C28.4922 32.4023 28.5898 32.9531 28.5898 33.5586V33.916C28.5898 34.5176 28.4922 35.0684 28.2969 35.5684C28.1055 36.0684 27.834 36.5 27.4824 36.8633C27.1309 37.2266 26.7148 37.5078 26.2344 37.707C25.7578 37.9023 25.2344 38 24.6641 38ZM23.9492 29.4688V38H21.8926V29.4688H23.9492ZM31.7188 29.4688V38H29.6621V29.4688H31.7188ZM35.0352 33.0195V34.6016H31.1328V33.0195H35.0352ZM35.3691 29.4688V31.0566H31.1328V29.4688H35.3691Z" fill="#FA0F00"/>
					</g>
				</svg>
			}
		/>
	);
}

export function DocumentIconCode(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="50" height="64" viewBox="0 0 50 64" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M49 4C49 2.34315 47.6569 1 46 1H15.5357C14.7767 1 14.0459 1.28769 13.4906 1.80509L1.95492 12.5533C1.34588 13.1208 1 13.9158 1 14.7482V60C1 61.6568 2.34315 63 4 63H46C47.6569 63 49 61.6569 49 60V4Z" fill="white" stroke="#FA7800" strokeWidth="2"/>
						<path d="M14.5 13.5V1.15061L1.26851 13.5H14.5Z" fill="#FA7800" stroke="#FA7800"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M16.7073 38.293L15.293 39.7072L8.58594 33.0001L15.293 26.293L16.7073 27.7072L11.4144 33.0001L16.7073 38.293Z" fill="#FA7800"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M34.2927 38.293L35.707 39.7072L42.4141 33.0001L35.707 26.293L34.2927 27.7072L39.5856 33.0001L34.2927 38.293Z" fill="#FA7800"/>
						<rect x="30" y="24" width="2" height="20" transform="rotate(30 30 24)" fill="#FA7800"/>
					</g>
				</svg>
			}
		/>
	);
}

export function DocumentIconText(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="50" height="64" viewBox="0 0 50 64" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M49 4C49 2.34315 47.6569 1 46 1H15.5357C14.7767 1 14.0459 1.28769 13.4906 1.80509L1.95492 12.5533C1.34588 13.1208 1 13.9158 1 14.7482V60C1 61.6568 2.34315 63 4 63H46C47.6569 63 49 61.6569 49 60V4Z" fill="white" stroke="#2864BE" strokeWidth="2"/>
						<path d="M14.5 13.5V1.15061L1.26851 13.5H14.5Z" fill="#2864BE" stroke="#2864BE"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M34.9736 29L34.9736 27L14.9736 27L14.9736 29L34.9736 29ZM28.9736 31.0002L28.9736 33.0002L14.9736 33.0002L14.9736 31.0002L28.9736 31.0002ZM34.9736 36.9999L34.9736 34.9999L14.9736 34.9999L14.9736 36.9999L34.9736 36.9999ZM28.9736 40.9999L28.9736 38.9999L14.9736 38.9999L14.9736 40.9999L28.9736 40.9999Z" fill="#2864BE"/>
					</g>
				</svg>
			}
		/>
	);
}

export function DocumentIconSpreadsheet(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="50" height="64" viewBox="0 0 50 64" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M49 4C49 2.34315 47.6569 1 46 1H15.5357C14.7767 1 14.0459 1.28769 13.4906 1.80509L1.95492 12.5533C1.34588 13.1208 1 13.9158 1 14.7482V60C1 61.6568 2.34315 63 4 63H46C47.6569 63 49 61.6569 49 60V4Z" fill="white" stroke="#46B420" strokeWidth="2"/>
						<path d="M14.5 13.5V1.15061L1.26851 13.5H14.5Z" fill="#46B420" stroke="#46B420"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M22 24H34C35.1046 24 36 24.8954 36 26V39C36 40.1046 35.1046 41 34 41H22H20H16C14.8954 41 14 40.1046 14 39V26C14 24.8954 14.8954 24 16 24H20H22ZM20 26H16V29H20V26ZM22 29V26H34V29H22ZM20 31H16V34H20V31ZM22 34V31H34V34H22ZM20 36H16V39H20V36ZM22 39V36H34V39H22Z" fill="#46B420"/>
					</g>
				</svg>
			}
		/>
	);
}

export function DocumentIconVideo(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="50" height="64" viewBox="0 0 50 64" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<path d="M4 63H46C47.6569 63 49 61.6569 49 60V4C49 2.34315 47.6569 1 46 1H15.5357C14.7767 1 14.0459 1.28769 13.4906 1.80509L1.95492 12.5533C1.34588 13.1208 1 13.9158 1 14.7482V60C1 61.6569 2.34315 63 4 63Z" fill="white" stroke="#7C28BE" strokeWidth="2"/>
						<path d="M14.5 13.5V1.15061L1.26851 13.5H14.5Z" fill="#7C28BE" stroke="#7C28BE"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M35 24H15L15 40H35V24ZM15 22C13.8954 22 13 22.8954 13 24V40C13 41.1046 13.8954 42 15 42H35C36.1046 42 37 41.1046 37 40V24C37 22.8954 36.1046 22 35 22H15Z" fill="#7C28BE"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M31 32L22 27L22 37L31 32ZM24 33.6667L24 30.3333L27 32L24 33.6667Z" fill="#7C28BE"/>
					</g>
				</svg>
			}
		/>
	);
}

export function ThreeDots(props)
{
	return (
		<svg width="56" height="16" viewBox="0 0 56 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M0 8C0 12.4183 3.58172 16 8 16H48C52.4183 16 56 12.4183 56 8C56 3.58172 52.4183 0 48 0H8C3.58172 0 0 3.58172 0 8Z" fill="#45565E"/>
			<circle cx="14" cy="8" r="4" fill="#FCFCFC"/>
			<circle cx="28" cy="8" r="4" fill="#FCFCFC"/>
			<circle cx="42" cy="8" r="4" fill="#FCFCFC"/>
		</svg>
	);
}

export function CameraExport(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M18.5 19L15.4689 13.75L21.5311 13.75L18.5 19Z" fill="white"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M17.7141 7.857H8.71415V9.78557V13.6427V15.5713H14.7884L15.5307 16.857H7.42843V14.1249L3.57129 15.5713V7.857L7.42843 9.30343V6.57129H18.9999V12.25H17.7141V7.857ZM7.42843 12.7517V10.6766L4.857 9.71229V13.716L7.42843 12.7517Z" fill="white"/>
				</svg>

			}
		/>
	);
}

export function CameraImport(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M18.5 4L15.4689 9.25L21.5311 9.25L18.5 4Z" fill="white"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M15.2834 6.57129H7.42843V9.30343L3.57129 7.857V15.5713L7.42843 14.1249V16.857H18.9999V10.75H17.7141V15.5713H8.71415V13.6427V9.78557V7.857H14.5411L15.2834 6.57129ZM7.42843 12.7517V10.6766L4.857 9.71229V13.716L7.42843 12.7517Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function PinIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="8" height="7" viewBox="0 0 8 7" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="3.75" cy="3.5" r="3.5" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function SearchIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M7.33335 14.0001C3.65146 14.0001 0.666687 11.0153 0.666687 7.33341C0.666687 3.65152 3.65146 0.666748 7.33335 0.666748C11.0153 0.666748 14 3.65152 14 7.33341C14 8.87401 13.4775 10.2925 12.5999 11.4215L17.0893 15.9108L15.9108 17.0893L11.4214 12.6C10.2925 13.4775 8.87395 14.0001 7.33335 14.0001ZM12.3334 7.33341C12.3334 10.0948 10.0948 12.3334 7.33335 12.3334C4.57193 12.3334 2.33335 10.0948 2.33335 7.33341C2.33335 4.57199 4.57193 2.33341 7.33335 2.33341C10.0948 2.33341 12.3334 4.57199 12.3334 7.33341Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function ScopeGPTIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M9.72827 3.24669C9.85467 2.91777 10.3564 2.91777 10.4828 3.24669C11.0173 4.63791 12.0449 7.05731 13.0726 8.07153C14.1029 9.08844 16.4308 10.1053 17.7571 10.6317C18.081 10.7603 18.081 11.2398 17.7571 11.3684C16.4307 11.8948 14.1029 12.9117 13.0726 13.9284C12.0449 14.9427 11.0173 17.3622 10.4828 18.7534C10.3564 19.0822 9.85467 19.0822 9.72827 18.7534C9.19376 17.3622 8.16612 14.9427 7.1385 13.9284C6.11086 12.9142 3.65953 11.9 2.24996 11.3725C1.91668 11.2477 1.91668 10.7525 2.24994 10.6278C3.65951 10.1001 6.11086 9.0858 7.1385 8.07153C8.16612 7.05731 9.19376 4.63791 9.72827 3.24669Z" fill="#EBEBEB"/>
					<path d="M1.63246 2.22308C1.77043 1.92564 2.25479 1.92564 2.39276 2.22308C2.54941 2.56076 2.73829 2.90349 2.92716 3.09118C3.11854 3.28137 3.45522 3.47155 3.78202 3.62843C4.07266 3.76796 4.07266 4.23209 3.78202 4.3716C3.4552 4.52846 3.11854 4.71864 2.92716 4.90882C2.73829 5.09651 2.54941 5.43924 2.39276 5.77692C2.25479 6.07436 1.77043 6.07436 1.63246 5.77692C1.4758 5.43924 1.28693 5.09651 1.09806 4.90882C0.909194 4.72113 0.564298 4.53345 0.224491 4.37778C-0.074821 4.24067 -0.0748348 3.75935 0.224477 3.62224C0.564284 3.46657 0.909194 3.27887 1.09806 3.09118C1.28693 2.90349 1.4758 2.56076 1.63246 2.22308Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function ScopeGPTProgressIcon(props)
{
	return (
		<div className={"scopegpt-progress"}></div>
	);
}
export function HtmlUITemplateIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M3 5C3 4.44772 3.44772 4 4 4H20C20.5523 4 21 4.44772 21 5V8H3V5Z" fill="white"/>
					<path d="M3 10H7V20H4C3.44772 20 3 19.5523 3 19V10Z" fill="white"/>
					<rect x="9" y="10" width="12" height="4" fill="white"/>
					<rect x="9" y="16" width="5" height="4" fill="white"/>
					<path d="M16 16H21V19C21 19.5523 20.5523 20 20 20H16V16Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function HtmlUITemplateIconOutline(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<mask id="path-1-outside-1_2545_44274" maskUnits="userSpaceOnUse" x="2" y="2.88867" width="20" height="8" fill="transparent">
						<rect fill="white" x="2" y="2.88867" width="20" height="8"/>
						<path fillRule="evenodd" clipRule="evenodd" d="M4 6.38867C4 5.56024 4.67157 4.88867 5.5 4.88867H18.5C19.3284 4.88867 20 5.56024 20 6.38867V8.44423H4V6.38867Z"/>
					</mask>
					<path d="M20 8.44423V9.94423H21.5V8.44423H20ZM4 8.44423H2.5V9.94423H4V8.44423ZM5.5 3.38867C3.84315 3.38867 2.5 4.73182 2.5 6.38867H5.5V6.38867V3.38867ZM18.5 3.38867H5.5V6.38867H18.5V3.38867ZM21.5 6.38867C21.5 4.73182 20.1569 3.38867 18.5 3.38867V6.38867L18.5 6.38867H21.5ZM21.5 8.44423V6.38867H18.5V8.44423H21.5ZM4 9.94423H20V6.94423H4V9.94423ZM2.5 6.38867V8.44423H5.5V6.38867H2.5Z" fill="white" mask="url(#path-1-outside-1_2545_44274)"/>
					<path d="M4 9.25H3.25V10V17.5C3.25 18.7426 4.25736 19.75 5.5 19.75H8H8.75V19V10V9.25H8H4Z" stroke="white" strokeWidth="1.5"/>
					<rect x="8.25" y="9.25" width="12.5" height="5.5" stroke="white" strokeWidth="1.5"/>
					<rect x="8.25" y="15.25" width="6.5" height="4.5" stroke="white" strokeWidth="1.5"/>
					<path d="M16 15.25H15.25V16V19V19.75H16H18.5C19.7426 19.75 20.75 18.7426 20.75 17.5V16V15.25H20H16Z" stroke="white" strokeWidth="1.5"/>
				</svg>
			}
		/>
	);
}

export function SwapIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M15.0421 9.43851L4.51838 9.43851L7.52113 6.43576L6.72945 5.64408L1.81535 10.559L4.51838 10.5581L15.0421 10.5581L15.0421 9.43851Z" fill="#EBEBEB"/>
					<path d="M2.37443 12.6943L12.8981 12.6943L9.89537 15.6971L10.6871 16.4887L15.6012 11.5738L12.8981 11.5747L2.37443 11.5747L2.37443 12.6943Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

// taken from https://www.unicode.org/L2/L2018/18303-external-link.pdf
export function ExternalLinkIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="48" height="48" viewBox="0 0 48 48">
					<path d="M36 24c-1.2 0-2 0.8-2 2v12c0 1.2-0.8 2-2 2h-22c-1.2 0-2-0.8-2-2v-22c0-1.2 0.8-2 2-2h12c1.2 0 2-0.8 2-2s-0.8-2-2-2h-12c-3.4 0-6 2.6-6 6v22c0 3.4 2.6 6 6 6h22c3.4 0 6-2.6 6-6v-12c0-1.2-0.8-2-2-2z" fill="#EBEBEB" />
					<path d="M43.8 5.2c-0.2-0.4-0.6-0.8-1-1-0.2-0.2-0.6-0.2-0.8-0.2h-12c-1.2 0-2 0.8-2 2s0.8 2 2 2h7.2l-18.6 18.6c-0.8 0.8-0.8 2 0 2.8 0.4 0.4 0.8 0.6 1.4 0.6s1-0.2 1.4-0.6l18.6-18.6v7.2c0 1.2 0.8 2 2 2s2-0.8 2-2v-12c0-0.2 0-0.6-0.2-0.8z" fill="#EBEBEB" />
				</svg>
			}
		/>
	);
}

export function FlexColumnIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M8 5H12" stroke="#434141" strokeWidth="2"/>
					<path d="M8 10H12" stroke="#434141" strokeWidth="2"/>
					<path d="M8 15H12" stroke="#434141" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function MenuIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="8.3125" y="13.0625" width="21.375" height="2.375" rx="1.1875" fill="#EBEBEB"/>
					<rect x="8.3125" y="17.8125" width="21.375" height="2.375" rx="1.1875" fill="#EBEBEB"/>
					<rect x="8.3125" y="22.5625" width="21.375" height="2.375" rx="1.1875" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function FlexRowIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M14 10H18" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M8 10H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 10H6" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function AttachmentsIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M20.1193 14.4529L21.3068 15.6224L16.0417 20.9688C15.7775 21.2385 15.7775 21.6724 16.0417 21.9421C16.2924 22.198 16.7112 22.1968 16.9592 21.9407L22.752 16.056C23.5977 15.193 23.5977 13.807 22.752 12.944C21.9452 12.1205 20.5175 12.1389 19.7267 12.9455L13.404 19.364C11.9765 20.8202 11.9765 23.1585 13.404 24.6147C14.7902 26.0287 17.1714 26.0057 18.5359 24.6134L25.9101 17.124L27.0977 18.2933L19.7249 25.7813C17.7133 27.8339 14.2586 27.8673 12.2138 25.7814C10.151 23.6771 10.151 20.3015 12.2152 18.1958L18.538 11.7773C19.9742 10.3123 22.4753 10.2802 23.9424 11.7775C25.4231 13.2885 25.4231 15.7114 23.9411 17.2239L18.1518 23.105C17.2562 24.0299 15.758 24.0341 14.8511 23.1085C13.9521 22.1908 13.9521 20.7201 14.8526 19.8009L20.1193 14.4529Z" fill="#ECEFF1"/>
				</svg>
			}
		/>
	);
}

export function FlexWrapIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M14 4H18" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M14 10H18" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M8 4H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M8 10H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 4H6" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 10H6" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 16H6" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function PartsCatalogIcon(props) 
{
	return (
		<svg transform={props.transform ?? `scale(${props.scale ?? 1}) rotate(${props.rotate ?? 0})`} width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect x="14.3409" y="14.3408" width="10.5909" height="3.31818" rx="1.25" stroke={props.fillStyle ? fillColors[props.fillStyle] : "#ECEFF1"} strokeWidth="1.5"/>
			<rect x="7.06824" y="7.97705" width="10.5909" height="3.31818" rx="1.25" stroke={props.fillStyle ? fillColors[props.fillStyle] : "#ECEFF1"} strokeWidth="1.5"/>
			<rect x="14.3409" y="20.7046" width="10.5909" height="3.31818" rx="1.25" stroke={props.fillStyle ? fillColors[props.fillStyle] : "#ECEFF1"} strokeWidth="1.5"/>
			<path d="M9.63635 11.4546L9.63635 15.5C9.63635 15.7762 9.86021 16 10.1364 16L14.1818 16" stroke={props.fillStyle ? fillColors[props.fillStyle] : "#ECEFF1"} strokeWidth="1.5"/>
			<path d="M9.63635 11.4546L9.63635 21.8637C9.63635 22.1398 9.86021 22.3637 10.1364 22.3637L14.1818 22.3637" stroke={props.fillStyle ? fillColors[props.fillStyle] : "#ECEFF1"} strokeWidth="1.5"/>
		</svg>
	);
}

export function FormIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M16.4966 10.6668C17.1235 10.136 18.0326 9.8335 19 9.8335C19.9674 9.8335 20.8765 10.136 21.5034 10.6668H22.3333C23.2538 10.6668 24 11.413 24 12.3335H24.8333C25.7538 12.3335 26.5 13.0797 26.5 14.0002V26.5002C26.5 27.4206 25.7538 28.1668 24.8333 28.1668H13.1667C12.2462 28.1668 11.5 27.4206 11.5 26.5002V14.0002C11.5 13.0797 12.2462 12.3335 13.1667 12.3335H14C14 11.413 14.7462 10.6668 15.6667 10.6668H16.4966ZM22.3333 14.8335C22.9502 14.8335 23.4889 14.4983 23.777 14.0002H24.8333V26.5002H13.1667V14.0002H14.223C14.5111 14.4983 15.0498 14.8335 15.6667 14.8335H22.3333ZM15.6667 22.3335V20.6668H20.6667V22.3335H15.6667ZM15.6667 17.3335V19.0002H22.3333V17.3335H15.6667ZM17.2147 12.3335L17.4639 12.0463C17.7377 11.7308 18.3245 11.5002 19 11.5002C19.6755 11.5002 20.2623 11.7308 20.5361 12.0463L20.7853 12.3335H22.3333V13.1668H15.6667V12.3335H16.8345H17.2147Z" fill="#ECEFF1"/>
				</svg>

			}
		/>
	);
}

export function FlexUpperLeftIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M2 4H10" stroke="#434141" strokeWidth="2"/>
					<path d="M2 7H8" stroke="#434141" strokeWidth="2"/>
					<path d="M2 10H6" stroke="#434141" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexUpperCenterIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M4 3H14" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M6 6H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M7 9H11" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexUpperRightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M8.11719 4L16.1172 4" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M10.1172 7H16.1172" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M12.1172 10H16.1172" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexMiddleLeftIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M2 12H6" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 6H6" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 9H8" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexMiddleCenterIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M7 6H11" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M6 9H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M7 12H11" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexMiddleRightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M16 12H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M16 6H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M16 9H10" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexLowerLeftIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M2 9H6" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 12H8" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M2 15H10" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexLowerCenterIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M7 9H11" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M6 12H12" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M4 15H14" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function FlexLowerRightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M12 9H16" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M10 12H16" stroke="#CDCDCD" strokeWidth="2"/>
					<path d="M8 15L16 15" stroke="#CDCDCD" strokeWidth="2"/>
				</svg>
			}
		/>
	);
}

export function BoxDashesIcon(props)
{
	props = {
		"data-name": "BoxDashesIcon",
		...props,
	};

	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M1 4L1 10" stroke="#EBEBEB" strokeWidth="1.5"/>
					<path d="M12.625 4L12.625 10" stroke="#EBEBEB" strokeWidth="1.5"/>
					<path d="M10 0.8125L4 0.812499" stroke="#EBEBEB" strokeWidth="1.5"/>
					<path d="M10 12.4375L4 12.4375" stroke="#EBEBEB" strokeWidth="1.5"/>
				</svg>
			}
		/>
	);
}

export function BoxTwoSidesIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<rect x="1.5" y="1" width="14" height="14" stroke="#303030" strokeWidth="2" fill="none"/>
						<path d="M0.5 1H16.5" stroke="#EBEBEB" strokeWidth="2"/>
						<path d="M0.5 15H16.5" stroke="#EBEBEB" strokeWidth="2"/>
					</g>
				</svg>
			}
		/>
	);
}

export function BoxOneSideIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<rect x="16" y="1.5" width="14" height="14" transform="rotate(90 16 1.5)" stroke="#303030" strokeWidth="2" fill="none"/>
						<path d="M1 0.5L0.999999 16.5" stroke="#EBEBEB" strokeWidth="2"/>
					</g>
				</svg>
			}
		/>
	);
}

export function ArrowTwoHeadsIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M7.91683 6.25005L7.08351 5.41673L10.0001 2.5001L12.9168 5.41673L12.0834 6.25005L10.5894 4.756L10.5894 15.244L12.0834 13.7499L12.9168 14.5833L10.0001 17.4999L7.08351 14.5833L7.91684 13.7499L9.41089 15.244L9.41089 4.756L7.91683 6.25005Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function PlusIconMenu(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M9 7H14V9H9V14H7V9H2V7H7V2H9V7Z" fill="#EBEBEB"/>
					<path d="M14 17L11.4019 12.5L16.5981 12.5L14 17Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function SquareSolidIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="4" y="4" width="12" height="12" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function SquareGradientIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g>
						<rect x="4" y="4" width="12" height="12" fill="url(#paint0_linear_4541_105693)"/>
					</g>
					<defs>
						<linearGradient id="paint0_linear_4541_105693" x1="10" y1="4" x2="10" y2="16" gradientUnits="userSpaceOnUse">
							<stop stopColor="#5E5E5E"/>
							<stop offset="1" stopColor="white"/>
						</linearGradient>
					</defs>
				</svg>
			}
		/>
	);
}

export function SquareCornersIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M6.5 3L3 3L3 6.5L4.75 6.5L4.75 4.75L6.5 4.75V3Z" fill="#EBEBEB"/>
					<path d="M13.5 4.75V3L17 3V6.5L15.25 6.5V4.75H13.5Z" fill="#EBEBEB"/>
					<path d="M13.5 15.25H15.25V13.5H17V17H13.5V15.25Z" fill="#EBEBEB"/>
					<path d="M4.75 13.5V15.25H6.5V17H3L3 13.5H4.75Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function CornerIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M9 1.75C9 0.783502 8.2165 0 7.25 0L4 0C1.79086 0 0 1.79086 0 4L0 7.25C0 8.2165 0.783502 9 1.75 9C2.7165 9 3.5 8.2165 3.5 7.25L3.5 6.25C3.5 4.73122 4.73122 3.5 6.25 3.5L7.25 3.5C8.2165 3.5 9 2.7165 9 1.75Z" fill="#D9D9D9"/>
				</svg>
			}
		/>
	);
}

export function LetterSpacingIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M1.99967 14.6673L1.99967 1.33398L3.33301 1.33398L3.33301 14.6673L1.99967 14.6673Z" fill="#EBEBEB"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M15.0007 14.6673L15.0007 1.33398L16.334 1.33398L16.334 14.6673L15.0007 14.6673Z" fill="#EBEBEB"/>
					<path d="M10.0781 10.5703V7.74609C10.0781 7.54297 10.0449 7.36914 9.97852 7.22461C9.91211 7.07617 9.80859 6.96094 9.66797 6.87891C9.53125 6.79688 9.35352 6.75586 9.13477 6.75586C8.94727 6.75586 8.78516 6.78906 8.64844 6.85547C8.51172 6.91797 8.40625 7.00977 8.33203 7.13086C8.25781 7.24805 8.2207 7.38672 8.2207 7.54688H6.5332C6.5332 7.27734 6.5957 7.02148 6.7207 6.7793C6.8457 6.53711 7.02734 6.32422 7.26562 6.14062C7.50391 5.95312 7.78711 5.80664 8.11523 5.70117C8.44727 5.5957 8.81836 5.54297 9.22852 5.54297C9.7207 5.54297 10.1582 5.625 10.541 5.78906C10.9238 5.95312 11.2246 6.19922 11.4434 6.52734C11.666 6.85547 11.7773 7.26562 11.7773 7.75781V10.4707C11.7773 10.8184 11.7988 11.1035 11.8418 11.3262C11.8848 11.5449 11.9473 11.7363 12.0293 11.9004V12H10.3242C10.2422 11.8281 10.1797 11.6133 10.1367 11.3555C10.0977 11.0938 10.0781 10.832 10.0781 10.5703ZM10.3008 8.13867L10.3125 9.09375H9.36914C9.14648 9.09375 8.95312 9.11914 8.78906 9.16992C8.625 9.2207 8.49023 9.29297 8.38477 9.38672C8.2793 9.47656 8.20117 9.58203 8.15039 9.70312C8.10352 9.82422 8.08008 9.95703 8.08008 10.1016C8.08008 10.2461 8.11328 10.377 8.17969 10.4941C8.24609 10.6074 8.3418 10.6973 8.4668 10.7637C8.5918 10.8262 8.73828 10.8574 8.90625 10.8574C9.16016 10.8574 9.38086 10.8066 9.56836 10.7051C9.75586 10.6035 9.90039 10.4785 10.002 10.3301C10.1074 10.1816 10.1621 10.041 10.166 9.9082L10.6113 10.623C10.5488 10.7832 10.4629 10.9492 10.3535 11.1211C10.248 11.293 10.1133 11.4551 9.94922 11.6074C9.78516 11.7559 9.58789 11.8789 9.35742 11.9766C9.12695 12.0703 8.85352 12.1172 8.53711 12.1172C8.13477 12.1172 7.76953 12.0371 7.44141 11.877C7.11719 11.7129 6.85938 11.4883 6.66797 11.2031C6.48047 10.9141 6.38672 10.5859 6.38672 10.2188C6.38672 9.88672 6.44922 9.5918 6.57422 9.33398C6.69922 9.07617 6.88281 8.85938 7.125 8.68359C7.37109 8.50391 7.67773 8.36914 8.04492 8.2793C8.41211 8.18555 8.83789 8.13867 9.32227 8.13867H10.3008Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function LineHeightIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fillRule="evenodd" clipRule="evenodd" d="M15 1L1 1L1 0L15 0V1Z" fill="#EBEBEB"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M15 16L1 16L1 15L15 15V16Z" fill="#EBEBEB"/>
					<path fillRule="evenodd" clipRule="evenodd" d="M15 16L1 16L1 15L15 15V16Z" fill="#EBEBEB"/>
					<path d="M5.84995 6.15546L7.54349 4.46191V8V8.125H7.66849H8.27455H8.39955V8V4.46191L10.0931 6.15546L10.1815 6.24384L10.2699 6.15546L10.6984 5.72691L10.7868 5.63852L10.6984 5.55013L8.05991 2.91161L7.97152 2.82322L7.88314 2.91161L5.24462 5.55013L5.15623 5.63852L5.24462 5.72691L5.67317 6.15546L5.76156 6.24384L5.84995 6.15546Z" fill="#EBEBEB" stroke="#EBEBEB" strokeWidth="0.25"/>
					<path d="M10.0934 9.84454L8.39987 11.5381L8.39987 8L8.39987 7.875L8.27487 7.875L7.66881 7.875L7.54381 7.875L7.54381 8L7.5438 11.5381L5.85026 9.84454L5.76187 9.75616L5.67348 9.84454L5.24493 10.2731L5.15654 10.3615L5.24493 10.4499L7.88345 13.0884L7.97183 13.1768L8.06022 13.0884L10.6987 10.4499L10.7871 10.3615L10.6987 10.2731L10.2702 9.84454L10.1818 9.75616L10.0934 9.84454Z" fill="#EBEBEB" stroke="#EBEBEB" strokeWidth="0.25"/>
				</svg>
			}
		/>
	);
}

export function BlurIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<g filter="url(#filter0_f_5304_49665)">
						<circle cx="8" cy="8" r="4" fill="#D9D9D9"/>
					</g>
					<defs>
						<filter id="filter0_f_5304_49665" x="0" y="0" width="16" height="16" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
							<feFlood flood-opacity="0" result="BackgroundImageFix"/>
							<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
							<feGaussianBlur stdDeviation="2" result="effect1_foregroundBlur_5304_49665"/>
						</filter>
					</defs>
				</svg>
			}
		/>
	);
}

export function SpreadIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<circle cx="8" cy="8" r="5.5" stroke="#EBEBEB"/>
					<g filter="url(#filter0_f_4663_43192)">
						<circle cx="8" cy="8" r="4" fill="#EBEBEB"/>
					</g>
					<defs>
						<filter id="filter0_f_4663_43192" x="0" y="0" width="16" height="16" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
							<feFlood flood-opacity="0" result="BackgroundImageFix"/>
							<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
							<feGaussianBlur stdDeviation="2" result="effect1_foregroundBlur_4663_43192"/>
						</filter>
					</defs>
				</svg>
			}
		/>
	);
}

export function NoneSquareIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M0 0L12 4.29136e-07L12 12H5.24537e-07L0 0ZM10 3.30792L3.30792 10H10V3.30792ZM8.47949 2L2 2V8.47949L8.47949 2Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function SkinnedMeshIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M21.2857 2H2.71429C2.52485 2 2.34316 2.07525 2.20921 2.20921C2.07525 2.34316 2 2.52485 2 2.71429V21.2857C2 21.4752 2.07525 21.6568 2.20921 21.7908C2.34316 21.9247 2.52485 22 2.71429 22H21.2857C21.4752 22 21.6568 21.9247 21.7908 21.7908C21.9247 21.6568 22 21.4752 22 21.2857V2.71429C22 2.52485 21.9247 2.34316 21.7908 2.20921C21.6568 2.07525 21.4752 2 21.2857 2ZM10.8143 20.5714C10.0519 19.073 9.48994 17.4807 9.14286 15.8357C10.0132 15.685 10.9074 15.7562 11.7429 16.0429C12.768 16.4163 13.8661 16.5459 14.95 16.4214C15.2725 17.853 15.7516 19.2447 16.3786 20.5714H10.8143ZM12.2429 14.7214C11.1865 14.3416 10.0511 14.2359 8.94286 14.4143C8.89818 13.6859 9.0052 12.9563 9.25714 12.2714C9.66563 11.1091 9.75444 9.85839 9.51429 8.65C10.2553 8.56727 11.0054 8.65523 11.7071 8.90714C12.852 9.31077 14.0793 9.42368 15.2786 9.23571C15.4118 10.0746 15.3357 10.9333 15.0571 11.7357C14.6768 12.7841 14.5495 13.9074 14.6857 15.0143C13.8668 15.0977 13.0397 14.9976 12.2643 14.7214H12.2429ZM3.42857 10.7857C4.90026 9.93556 6.48169 9.29145 8.12857 8.87143C8.32332 9.8228 8.26169 10.8088 7.95 11.7286C7.6048 12.6688 7.47068 13.6735 7.55714 14.6714C6.12435 15.0517 4.73931 15.5933 3.42857 16.2857V10.7857ZM13.2143 3.42857C14.0129 4.81608 14.6321 6.29937 15.0571 7.84286C14.1209 7.98256 13.1647 7.89185 12.2714 7.57857C11.3012 7.22442 10.2599 7.10927 9.23571 7.24286C8.86003 5.92285 8.35023 4.64477 7.71429 3.42857H13.2143ZM16.4286 12.2643C16.8051 11.2147 16.9107 10.087 16.7357 8.98571C18.0562 8.66206 19.3412 8.20742 20.5714 7.62857V13.1786C19.1633 13.8952 17.674 14.4396 16.1357 14.8C16.0348 13.944 16.1326 13.0763 16.4214 12.2643H16.4286ZM20.5714 6.04286C19.2654 6.70749 17.8905 7.22725 16.4714 7.59286C16.0938 6.14779 15.5521 4.75062 14.8571 3.42857H20.5714V6.04286ZM6.07143 3.42857C6.8053 4.70751 7.39007 6.06638 7.81429 7.47857C6.29011 7.85281 4.81731 8.41171 3.42857 9.14286V3.42857H6.07143ZM3.42857 17.9286C4.77756 17.1508 6.21689 16.5415 7.71429 16.1143C8.03802 17.6555 8.54645 19.152 9.22857 20.5714H3.42857V17.9286ZM17.9571 20.5714C17.2518 19.1874 16.7124 17.7249 16.35 16.2143C17.8037 15.897 19.2173 15.4178 20.5643 14.7857V20.5714H17.9571Z" fill="white"/>
				</svg>

			}
		/>
	);
}

export function BoneIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M1 14C1 16.2091 2.79086 18 5 18C6.45671 18 7.76392 17.2142 8.46504 16H15.535C16.2361 17.2142 17.5433 18 19 18C21.2091 18 23 16.2091 23 14C23 13.2857 22.8114 12.5995 22.4648 12C22.8114 11.4005 23 10.7143 23 10C23 7.79086 21.2091 6 19 6C17.5433 6 16.2361 6.78584 15.535 8H8.46504C7.76392 6.78584 6.45671 6 5 6C2.79086 6 1 7.79086 1 10C1 10.7143 1.18859 11.4005 1.53521 12C1.18859 12.5995 1 13.2857 1 14ZM7.12212 14L6.88645 14.6668C6.6064 15.4591 5.85452 16 5 16C3.89543 16 3 15.1046 3 14C3 13.5002 3.18269 13.0315 3.50903 12.6669L4.10593 12L3.50903 11.3331C3.18269 10.9685 3 10.4998 3 10C3 8.89543 3.89543 8 5 8C5.85452 8 6.6064 8.5409 6.88645 9.33325L7.12212 10H16.8779L17.1135 9.33325C17.3936 8.5409 18.1455 8 19 8C20.1046 8 21 8.89543 21 10C21 10.4998 20.8173 10.9685 20.491 11.3331L19.8941 12L20.491 12.6669C20.8173 13.0315 21 13.5002 21 14C21 15.1046 20.1046 16 19 16C18.1455 16 17.3936 15.4591 17.1135 14.6668L16.8779 14H7.12212Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function LayersIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M13 5L7 1L1 5L7 9L13 5Z" fill="white"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M1.75 7.5L7 11L12.25 7.5L13 8L7 12L1 8L1.75 7.5Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function DateSelectionIcon(props)
{
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="22"
			height="22"
			viewBox="0 0 19 19"
			stroke="#A6A6A6"
			strokeWidth="2"
		>
			<rect fill="none" height="15" width="15" x="2" y="2" />
			<line x1="6" x2="6" y1="0" y2="4" />
			<line x1="13" x2="13" y1="0" y2="4" />
		</svg>
	);
}

export function LockIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path id="icon" fill-rule="evenodd" clip-rule="evenodd" d="M6 6V9C4.39747 9 3 10.1646 3 11.75V21.25C3 22.8354 4.39747 24 6 24H18C19.6025 24 21 22.8354 21 21.25V11.75C21 10.1646 19.6025 9 18 9V6C18 2.68629 15.3137 0 12 0C8.68629 0 6 2.68629 6 6ZM15 6V9H9V6C9 4.34315 10.3431 3 12 3C13.6569 3 15 4.34315 15 6ZM6 21V12H18V21H6ZM13.5 16.5C13.5 17.3284 12.8284 18 12 18C11.1716 18 10.5 17.3284 10.5 16.5C10.5 15.6716 11.1716 15 12 15C12.8284 15 13.5 15.6716 13.5 16.5Z" fill="black"/>
				</svg>
			}
		/>
	);
}

export function GjsIconContainer(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="60" height="44" viewBox="0 0 60 44" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="9" y="10" width="42" height="24" stroke="#EBEBEB" stroke-width="2" stroke-dasharray="4 4" fill="none"/>
				</svg>
			}
		/>
	);
}

export function FrameIcon(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M0.5 6H4.5L6 8H10L11.5 6H15" stroke="white" stroke-width="1.5"/>
					<mask id="path-2-inside-1_2537_13741" fill="white">
						<path fill-rule="evenodd" clip-rule="evenodd" d="M14.5 1.5H1.5V14.5H14.5V1.5ZM0 0V16H16V0H0Z"/>
					</mask>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M14.5 1.5H1.5V14.5H14.5V1.5ZM0 0V16H16V0H0Z" fill="white"/>
					<path d="M1.5 1.5V0H0V1.5H1.5ZM14.5 1.5H16V0H14.5V1.5ZM1.5 14.5H0V16H1.5V14.5ZM14.5 14.5V16H16V14.5H14.5ZM0 0V-1.5H-1.5V0H0ZM0 16H-1.5V17.5H0V16ZM16 16V17.5H17.5V16H16ZM16 0H17.5V-1.5H16V0ZM1.5 3H14.5V0H1.5V3ZM3 14.5V1.5H0V14.5H3ZM14.5 13H1.5V16H14.5V13ZM13 1.5V14.5H16V1.5H13ZM0 1.5H16V-1.5H0V1.5ZM1.5 16V0H-1.5V16H1.5ZM16 14.5H0V17.5H16V14.5ZM14.5 0V16H17.5V0H14.5Z" fill="white" mask="url(#path-2-inside-1_2537_13741)"/>
				</svg>
			}
		/>
	);
}

export function GjsIconText(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="60" height="44" viewBox="0 0 60 44" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M31.875 7.5625V36H28.1641V7.5625H31.875ZM41.0156 7.5625V10.6484H19.043V7.5625H41.0156Z" fill="#EBEBEB"/>
				</svg>
			}
		/>
	);
}

export function GjsIconImage(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M23.0004 2H3.00035V22H23.0004V2ZM1.00035 0V24H25.0004V0H1.00035Z" fill="white"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M8.33373 9.25247L21.7075 22.6262L20.2933 24.0405L8.33373 12.0809L2.37417 18.0405L0.959961 16.6262L8.33373 9.25247Z" fill="white"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M18.3337 8.66675C19.4382 8.66675 20.3337 7.77132 20.3337 6.66675C20.3337 5.56218 19.4382 4.66675 18.3337 4.66675C17.2291 4.66675 16.3337 5.56218 16.3337 6.66675C16.3337 7.77132 17.2291 8.66675 18.3337 8.66675ZM18.3337 10.6667C20.5428 10.6667 22.3337 8.87589 22.3337 6.66675C22.3337 4.45761 20.5428 2.66675 18.3337 2.66675C16.1245 2.66675 14.3337 4.45761 14.3337 6.66675C14.3337 8.87589 16.1245 10.6667 18.3337 10.6667Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function GjsIconVideo(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M22 4H2L2 20H22V4ZM2 2C0.895431 2 0 2.89543 0 4V20C0 21.1046 0.89543 22 2 22H22C23.1046 22 24 21.1046 24 20V4C24 2.89543 23.1046 2 22 2H2Z" fill="white"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M18 12L9 7L9 17L18 12ZM11 13.6667L11 10.3333L14 12L11 13.6667Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function GjsIconAudio(props)
{
	return (
		<BaseIcon
			{...props}
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M21 0L8.78339 5.553H2V17.553H8.78339L21 23.106V0ZM10 15.9091L19 20V3.10601L10 7.19692V15.9091ZM4 7.553H8V15.553H4V7.553Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function GjsIconCamera(props)
{
	return (
		<BaseIcon
			{...props}		
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M16 6H2V18H16V6ZM0 4V20H18V4H0Z" fill="white"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M18 10.386V13.614L22 15.114V8.886L18 10.386ZM16 9V15L24 18V6L16 9Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function InfoIconRound(props)
{
	return (
		<BaseIcon
			{...props}		
			svg={
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M0.666666 7.99996C0.666666 12.05 3.94991 15.3333 8 15.3333C12.0501 15.3333 15.3333 12.05 15.3333 7.99996C15.3333 3.94987 12.0501 0.666626 8 0.666626C3.94991 0.666626 0.666666 3.94987 0.666666 7.99996ZM14 7.99996C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 7.99996C2 4.68625 4.68629 1.99996 8 1.99996C11.3137 1.99996 14 4.68625 14 7.99996ZM8.66907 9.33219H9.3353V10.6655H6.66864V9.33219H7.3353V7.99886H6.66864V6.66552H8.66907V9.33219ZM8.6671 5.33219C8.6671 5.70038 8.36853 5.99886 8.00022 5.99886C7.63191 5.99886 7.33333 5.70038 7.33333 5.33219C7.33333 4.964 7.63191 4.66552 8.00022 4.66552C8.36853 4.66552 8.6671 4.964 8.6671 5.33219Z" fill="#A6A6A6"/>
				</svg>
			}
		/>
	);
}

export function TrackerImageIcon(props)
{
	return (
		<BaseIcon
			{...props}		
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M4 2H20C21.1046 2 22 2.89543 22 4V20C22 21.1046 21.1046 22 20 22H4C2.89543 22 2 21.1046 2 20V4C2 2.89543 2.89543 2 4 2ZM4 4V15.5858L8 11.5858L11.5 15.0858L18 8.58579L20 10.5858V4H4ZM4 20V18.4142L8 14.4142L13.5858 20H4ZM20 20H16.4142L12.9142 16.5L18 11.4142L20 13.4142V20ZM14 8C14 6.34315 12.6569 5 11 5C9.34315 5 8 6.34315 8 8C8 9.65685 9.34315 11 11 11C12.6569 11 14 9.65685 14 8ZM10 8C10 7.44772 10.4477 7 11 7C11.5523 7 12 7.44772 12 8C12 8.55228 11.5523 9 11 9C10.4477 9 10 8.55228 10 8Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function TrackerObjectIcon(props)
{
	return (
		<BaseIcon
			{...props}		
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M12 2L22 8.5V15.5L12 22L2 15.5V8.5L12 2Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M12 22V15.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M22 8.5L12 15.5L2 8.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			}
		/>
	);
}

export function TrackerSurfaceIcon(props)
{
	return (
		<BaseIcon
			{...props}		
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M18.416 5H5.58401L2.52518 18H21.4748L18.416 5ZM5.58401 3C4.65591 3 3.84975 3.63849 3.63717 4.54192L0.57835 17.5419C0.282959 18.7973 1.23549 20 2.52518 20H21.4748C22.7645 20 23.717 18.7973 23.4216 17.5419L20.3628 4.54192C20.1502 3.63849 19.3441 3 18.416 3H5.58401Z" fill="white"/>
				</svg>
			}
		/>
	);
}

export function ChatCommentIcon(props)
{
	return (
		<BaseIcon
			{...props}		
			svg={
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M6 21.8042L12.0868 18H20C21.1046 18 22 17.1046 22 16V4C22 2.89543 21.1046 2 20 2H4C2.89543 2 2 2.89543 2 4V16C2 17.1046 2.89543 18 4 18H6V21.8042ZM11.5132 16L8 18.1958V16H4V4H20V16H11.5132Z" fill="#ECEFF1"/>
				</svg>
			}
		/>
	);
}
